import React, { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import { actualizarPresupuestoTotalInicial, actualizarPresupuestoTotalActualizado, recalcularPresupuestosInicialesActualizados } from "./servicios/actualizarPresupuestosTotales";
import {
  Building2,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  CheckCircle2,
  Clock,
  MapPin,
  Ruler,
  DollarSign,
  FileText,
  Calendar,
  Loader2,
  ChevronDown,
  Package,
  Percent,
  TrendingUp,
  Upload,
  X,
  Save,
  User,
  Activity,
  AlertTriangle,
  RotateCcw,
  CreditCard,
  RefreshCw,
  Users,
  BarChart3,
} from "lucide-react";

// ✅ CONFIGURACIÓN ANTI-ZOOM OBLIGATORIA
if (typeof document !== "undefined") {
  let metaViewport = document.querySelector('meta[name="viewport"]');
  if (!metaViewport) {
    metaViewport = document.createElement("meta");
    metaViewport.name = "viewport";
    document.head.appendChild(metaViewport);
  }
  metaViewport.content =
    "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
}

// ✅ COLORES DE VISTA PROYECTOS
const getVistaColor = () => ({
  gradient: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
  solid: "#3b82f6",
  light: "#3b82f610",
  description: "Gestión de obras",
});

// ✅ DROPDOWN PERSONALIZADO UNIFICADO
const CustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  icon: Icon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const selected = options.find((opt) => opt.value === value);
    setSelectedOption(selected);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "14px 16px",
          border: "2px solid #f1f5f9",
          borderRadius: "12px",
          background: disabled ? "#f8fafc" : "white",
          color: disabled ? "#94a3b8" : "#1e293b",
          fontSize: "16px",
          fontWeight: "500",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          outline: "none",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "44px",
          touchAction: "manipulation",
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = "#3b82f6";
          e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "#f1f5f9";
          e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}
        >
          {Icon && <Icon size={16} color="#64748b" />}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            flexShrink: 0,
            color: "#64748b",
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
            marginTop: "4px",
            maxHeight: "200px",
            overflowY: "auto",
          }}
        >
          {options.map((option, index) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "none",
                background: option.value === value ? "#eff6ff" : "transparent",
                color: option.value === value ? "#3b82f6" : "#374151",
                fontSize: "16px",
                fontWeight: option.value === value ? "600" : "400",
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
              onMouseEnter={(e) => {
                if (option.value !== value) {
                  e.target.style.background = "#f8fafc";
                }
              }}
              onMouseLeave={(e) => {
                if (option.value !== value) {
                  e.target.style.background = "transparent";
                }
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Modal personalizado para alertas/confirmaciones
function CustomModal({
  isOpen,
  onClose,
  type = "info",
  title,
  message,
  onConfirm,
  confirmText = "Aceptar",
  cancelText = "Cancelar",
  isMobile,
  options = null,
}) {
  // Evitar scroll del fondo mientras el modal está abierto
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev || '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const getColors = () => {
    switch (type) {
      case "confirm":
        return {
          bg: "#fef3cd",
          border: "#f59e0b",
          text: "#92400e",
          button: "#f59e0b",
        };
      case "error":
        return {
          bg: "#fef2f2",
          border: "#ef4444",
          text: "#dc2626",
          button: "#ef4444",
        };
      case "success":
        return {
          bg: "#f0fdf4",
          border: "#22c55e",
          text: "#166534",
          button: "#22c55e",
        };
      default:
        return {
          bg: "#f0f9ff",
          border: "#3b82f6",
          text: "#1d4ed8",
          button: "#3b82f6",
        };
    }
  };

  const colors = getColors();

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10001, // Mayor que el modal principal (10000)
        padding: "20px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          padding: "24px",
          width: "100%",
          maxWidth: isMobile ? "90%" : "480px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          border: `2px solid ${colors.border}`,
          maxHeight: '80vh',
          overflowY: 'auto',
        }}
        onWheel={(e) => {
          const el = e.currentTarget;
          const atTop = el.scrollTop === 0;
          const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
          if ((atTop && e.deltaY < 0) || (atBottom && e.deltaY > 0)) {
            e.preventDefault();
          }
        }}
        onTouchMove={(e) => {
          const el = e.currentTarget;
          const atTop = el.scrollTop <= 0;
          const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
          if (atTop || atBottom) {
            e.preventDefault();
          }
        }}
      >
        <div
          style={{
            textAlign: "center",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              background: `${colors.button}20`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 12px",
            }}
          >
            {type === "confirm" && <FileText size={24} color={colors.button} />}
            {type === "error" && <X size={24} color={colors.button} />}
            {type === "success" && (
              <CheckCircle2 size={24} color={colors.button} />
            )}
            {type === "info" && <FileText size={24} color={colors.button} />}
          </div>
          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: colors.text,
            }}
          >
            {title}
          </h3>
          {typeof message === 'string' ? (
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#64748b",
                lineHeight: "1.4",
              }}
            >
              {message}
            </p>
          ) : (
            <div style={{ margin: 0 }}>
              {message}
            </div>
          )}
        </div>

        <div
          style={{
            display: options ? "grid" : "flex",
            gridTemplateColumns: options ? (isMobile ? "1fr" : `repeat(${1 + options.length}, 1fr)`) : undefined,
            gap: "12px",
            justifyContent: options ? undefined : "center",
          }}
        >
          {options ? (
            <>
              <button
                onClick={onClose}
                style={{
                  padding: "12px 16px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  background: "white",
                  color: "#64748b",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                {cancelText}
              </button>
              {options.map((opt, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    try { if (typeof opt.onClick === 'function') opt.onClick(); } finally { onClose(); }
                  }}
                  style={{
                    padding: "12px 16px",
                    border: "none",
                    borderRadius: "8px",
                    background: colors.button,
                    color: "white",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </>
          ) : (
            <>
              {type === "confirm" && (
                <button
                  onClick={onClose}
                  style={{
                    padding: "12px 16px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    background: "white",
                    color: "#64748b",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  {cancelText}
                </button>
              )}
              <button
                onClick={() => {
                  if (type === "confirm" && onConfirm) onConfirm();
                  onClose();
                }}
                style={{
                  padding: "12px 20px",
                  border: "none",
                  borderRadius: "8px",
                  background: colors.button,
                  color: "white",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                {confirmText}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Proyectos({ constructora }) {
  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("todos");
  const [error, setError] = useState(null);

  // Estados para alertas personalizadas
  const [customModal, setCustomModal] = useState({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
    onConfirm: null,
    confirmText: "Aceptar",
  });

  const colorVista = getVistaColor();

  // ✅ SCROLL RESET OBLIGATORIO al cambiar vista
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // ✅ ESCAPE handler obligatorio para cerrar modales
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setShowModal(false);
        setCustomModal((prev) => ({ ...prev, isOpen: false }));
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Bloquear scroll del body cuando hay algún modal abierto; restaurar al cerrar
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    if (showModal || customModal.isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showModal, customModal.isOpen]);

  useEffect(() => {
    console.log("🔍 Constructora recibida en Proyectos:", {
      constructora: constructora,
      constructoraId: constructora?.id,
      constructoraIdType: typeof constructora?.id,
      constructoraIdLength: constructora?.id?.length
    });

    if (constructora?.id) {
      // ✅ Validar que el ID sea un UUID válido
      if (typeof constructora.id === 'string' && constructora.id.length === 36) {
        cargarProyectos();
      } else {
        console.error("❌ Error: constructora.id no es un UUID válido:", {
          constructoraId: constructora.id,
          type: typeof constructora.id,
          length: constructora.id.length
        });
        setError("Error: ID de constructora inválido. Por favor, recarga la página.");
      }
    }
  }, [constructora]);

  const showCustomAlert = (type, title, message, onConfirm = null, options = null, confirmText = "Aceptar") => {
    setCustomModal({
      isOpen: true,
      type,
      title,
      message,
      onConfirm,
      options,
      confirmText,
    });
  };

  const cargarProyectos = async () => {
    try {
      const { data, error } = await supabase
        .from("proyectos")
        .select("*")
        .eq("constructora_id", constructora.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error cargando proyectos:", error);
      } else {
        setProyectos(data || []);
      }
    } catch (err) {
      console.error("Error:", err);
    }
    setLoading(false);
  };

  const filteredProyectos = proyectos.filter((proyecto) => {
    const matchesSearch =
      proyecto.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      proyecto.apellido_cliente
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const isActivo = proyecto.activo !== false && proyecto.deleted_at == null;
    const isEliminado = proyecto.activo === false || proyecto.deleted_at != null;

    if (filterStatus === "todos") return matchesSearch;
    if (filterStatus === "activos")
      return matchesSearch && isActivo && !proyecto.obra_finalizada;
    if (filterStatus === "finalizados")
      return matchesSearch && isActivo && proyecto.obra_finalizada;
    if (filterStatus === "eliminados")
      return matchesSearch && isEliminado;

    return matchesSearch;
  });



  const handleEliminar = async (id) => {
    try {
      const { data: impacto, error: errImp } = await supabase.rpc('rpc_proyecto_analizar_impacto', { p_proyecto_id: id });
      if (errImp) throw errImp;

      // Crear lista estética de elementos a eliminar
      const elementosAEliminar = [
        { icon: DollarSign, label: "Entradas", count: impacto.entradas },
        { icon: CreditCard, label: "Salidas", count: impacto.salidas },
        { icon: RefreshCw, label: "Cambios de moneda", count: impacto.cambios_moneda },
        { icon: Users, label: "Cobros a trabajadores", count: impacto.cobros_trabajadores },
        { icon: BarChart3, label: "Arqueos de caja", count: impacto.arqueos_caja },
        { icon: FileText, label: "Presupuestos", count: impacto.presupuestos },
        { icon: Plus, label: "Adicionales de presupuesto", count: impacto.adicionales_presupuesto },
        { icon: TrendingUp, label: "Presupuestos iniciales", count: impacto.presupuestos_iniciales },
        { icon: Calendar, label: "Tareas del cronograma", count: impacto.gantt_tareas },
        { icon: Building2, label: "Cajas", count: impacto.cajas }
      ].filter(item => item.count > 0);

      const resumen = `Se eliminarán ${elementosAEliminar.length} tipos de elementos:\n\n` +
        elementosAEliminar.map(item => `• ${item.label}: ${item.count}`).join('\n') +
        '\n\n⚠️ Esta acción no se puede deshacer. Todos los datos serán marcados como eliminados.';

      // Crear componente de contenido para el modal
      const ModalContent = () => (
        <div style={{ textAlign: 'left' }}>
          <p style={{ margin: '0 0 20px 0', color: '#64748b', fontSize: '14px' }}>
            Se eliminarán los siguientes elementos:
          </p>
          <div style={{ marginBottom: '20px' }}>
            {elementosAEliminar.map((item, index) => (
              <div key={index} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                padding: '8px 0',
                borderBottom: index < elementosAEliminar.length - 1 ? '1px solid #f1f5f9' : 'none'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <item.icon size={18} color="#64748b" />
                  <span style={{ color: '#374151', fontSize: '14px' }}>
                    {item.label}
                  </span>
                </div>
                <span style={{ 
                  background: '#ef4444', 
                  color: 'white', 
                  padding: '2px 8px', 
                  borderRadius: '12px', 
                  fontSize: '12px', 
                  fontWeight: '500' 
                }}>
                  {item.count}
                </span>
              </div>
            ))}
          </div>
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px',
            background: '#fef3cd',
            borderRadius: '6px',
            border: '1px solid #fde68a'
          }}>
            <AlertTriangle size={16} color="#f59e0b" />
            <span style={{ color: '#92400e', fontSize: '13px' }}>
              Los datos serán marcados como eliminados pero podrán ser restaurados posteriormente.
            </span>
          </div>
        </div>
      );

      showCustomAlert(
        "confirm",
        "Eliminar Proyecto",
        <ModalContent />,
        async () => {
          try {
            const { error } = await supabase.rpc('rpc_soft_delete_proyecto', {
              p_proyecto_id: id,
              p_usuario_id: constructora?.usuario_id || null,
              p_modo: 'cascada',
              p_razon: null,
            });
            if (error) throw error;
            try {
              await actualizarPresupuestoTotalInicial(id);
              await actualizarPresupuestoTotalActualizado(id);
            } catch (e) {
              console.error('⚠️ Error actualizando totales tras eliminar proyecto:', e);
            }
            await cargarProyectos();
            showCustomAlert('success','Éxito','Proyecto y datos relacionados eliminados.');
          } catch (e) {
            showCustomAlert('error','Error', e.message || 'No se pudo eliminar.');
          }
        },
        null,
        "Eliminar"
      );
    } catch (err) {
      showCustomAlert('error','Error', err.message || 'No se pudo analizar el impacto.');
    }
  };

  const handleRestaurar = async (id) => {
    try {
      // Analizar qué elementos se van a restaurar
      const { data: impacto, error: errImp } = await supabase.rpc('rpc_proyecto_analizar_impacto_restauracion', { p_proyecto_id: id });
      if (errImp) throw errImp;

      // Crear lista de elementos a restaurar con contadores
      const elementosARestaurar = [
        { icon: Building2, label: "Proyecto", count: 1 },
        { icon: DollarSign, label: "Entradas", count: impacto.entradas || 0 },
        { icon: CreditCard, label: "Salidas", count: impacto.salidas || 0 },
        { icon: RefreshCw, label: "Cambios de moneda", count: impacto.cambios_moneda || 0 },
        { icon: Users, label: "Cobros a trabajadores", count: impacto.cobros_trabajadores || 0 },
        { icon: BarChart3, label: "Arqueos de caja", count: impacto.arqueos_caja || 0 },
        { icon: FileText, label: "Presupuestos", count: impacto.presupuestos || 0 },
        { icon: Plus, label: "Adicionales de presupuesto", count: impacto.adicionales_presupuesto || 0 },
        { icon: TrendingUp, label: "Presupuestos iniciales", count: impacto.presupuestos_iniciales || 0 },
        { icon: Calendar, label: "Tareas del cronograma", count: impacto.gantt_tareas || 0 },
        { icon: Building2, label: "Cajas", count: impacto.cajas || 0 }
      ].filter(item => item.count > 0);

            // Crear componente de contenido para el modal de restauración
      const ModalContentRestaurar = () => (
        <div style={{ textAlign: 'left' }}>
          <p style={{ margin: '0 0 20px 0', color: '#64748b', fontSize: '14px' }}>
            Se restaurarán los siguientes elementos:
          </p>
          <div style={{ marginBottom: '20px' }}>
            {elementosARestaurar.map((item, index) => (
              <div key={index} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                padding: '8px 0',
                borderBottom: index < elementosARestaurar.length - 1 ? '1px solid #f1f5f9' : 'none'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <item.icon size={18} color="#64748b" />
                  <span style={{ color: '#374151', fontSize: '14px' }}>
                    {item.label}
                  </span>
                </div>
                <span style={{ 
                  background: '#22c55e', 
                  color: 'white', 
                  padding: '2px 8px', 
                  borderRadius: '12px', 
                  fontSize: '12px', 
                  fontWeight: '500' 
                }}>
                  {item.count}
                </span>
              </div>
            ))}
          </div>
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px',
            background: '#f0fdf4',
            borderRadius: '6px',
            border: '1px solid #bbf7d0'
          }}>
            <CheckCircle2 size={16} color="#22c55e" />
            <span style={{ color: '#166534', fontSize: '13px' }}>
              Todos los datos eliminados junto con el proyecto serán restaurados.
            </span>
          </div>
        </div>
      );

      showCustomAlert(
        'confirm',
        'Restaurar Proyecto',
        <ModalContentRestaurar />,
        async () => {
          try {
            const { error } = await supabase.rpc('rpc_restore_proyecto', {
              p_proyecto_id: id,
              p_usuario_id: constructora?.usuario_id || null,
              p_modo: 'cascada',
            });
            if (error) throw error;
            try {
              await actualizarPresupuestoTotalInicial(id);
              await actualizarPresupuestoTotalActualizado(id);
            } catch (e) {
              console.error('⚠️ Error actualizando totales tras restaurar proyecto:', e);
            }
            await cargarProyectos();
            showCustomAlert('success','Éxito','Proyecto y datos relacionados restaurados.');
          } catch (e) {
            showCustomAlert('error','Error', e.message || 'No se pudo restaurar.');
          }
        },
        null,
        "Restaurar"
      );
    } catch (err) {
      showCustomAlert('error','Error', err.message || 'No se pudo analizar el impacto de restauración.');
    }
  };

  const toggleObraFinalizada = async (proyectoId, estadoActual) => {
    try {
      const { error } = await supabase
        .from("proyectos")
        .update({
          obra_finalizada: !estadoActual,
          fecha_finalizacion: !estadoActual
            ? new Date().toISOString().split("T")[0]
            : null,
        })
        .eq("id", proyectoId);

      if (error) {
        showCustomAlert(
          "error",
          "Error",
          "Error al actualizar estado: " + error.message
        );
      } else {
        cargarProyectos();
      }
    } catch (err) {
      showCustomAlert("error", "Error", "Error: " + err.message);
    }
  };

  // Preparar opciones para el dropdown de filtros
  const opcionesFiltros = [
    { value: "todos", label: "Todos los proyectos" },
    { value: "activos", label: "Solo activos" },
    { value: "finalizados", label: "Solo finalizados" },
    { value: "eliminados", label: "Eliminados" },
  ];

  const handleChangeFilterStatus = (value) => {
    setFilterStatus(value);
    // Refrescar datos para reflejar cambios hechos desde fuera (SQL/RPC directos)
    cargarProyectos();
  };

  return (
    <div
      className="main-container"
      style={{
        padding: isMobile ? "16px 12px 120px 12px" : "20px",
        maxWidth: "100%",
        margin: "0 auto",
        background: "#f8fafc",
        minHeight: "100vh",
        animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* ✅ HEADER UNIFICADO SIGUIENDO LOS LINEAMIENTOS */}
      <div
        className="header-section"
        style={{
          marginBottom: "20px",
          animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "16px",
            minHeight: "56px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
              minWidth: "200px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: colorVista.gradient,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 16px rgba(59, 130, 246, 0.25)",
                flexShrink: 0,
              }}
            >
              <Building2 size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1
                style={{
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  color: "#0f172a",
                  margin: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Proyectos
              </h1>
              <p
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  margin: 0,
                  fontWeight: "400",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Gestiona todos los proyectos de {constructora?.nombre}
              </p>
            </div>
          </div>

          {/* Botón desktop unificado - SIEMPRE VISIBLE */}
          {!isMobile && (
            <button
              onClick={() => {
                setEditingProject(null);
                setShowModal(true);
              }}
              className="button-hover"
              style={{
                background: colorVista.gradient,
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: "12px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                boxShadow: `0 4px 16px ${colorVista.light}`,
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
                flexShrink: 0,
                minWidth: "max-content",
                whiteSpace: "nowrap",
              }}
            >
              <Plus size={16} />
              Nuevo Proyecto
            </button>
          )}
        </div>

        {/* ✅ FILTROS SIGUIENDO LOS LINEAMIENTOS */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr",
            gap: "16px",
            marginBottom: "20px",
          }}
        >
          <div style={{ position: "relative" }}>
            <Search
              size={16}
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#64748b",
                zIndex: 1,
              }}
            />
            <input
              type="text"
              placeholder="Buscar por código o cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus={false}
              autoComplete="off"
              style={{
                width: "100%",
                padding: "14px 16px 14px 44px",
                border: "2px solid #f1f5f9",
                borderRadius: "12px",
                fontSize: "16px",
                WebkitAppearance: "none",
                appearance: "none",
                background: "white",
                boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                outline: "none",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                boxSizing: "border-box",
                touchAction: "manipulation",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#3b82f6";
                e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#f1f5f9";
                e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
              }}
            />
          </div>

          <CustomDropdown
            value={filterStatus}
            onChange={handleChangeFilterStatus}
            options={opcionesFiltros}
            placeholder="Filtrar por estado"
            icon={Filter}
          />
        </div>
      </div>

      {/* ✅ MOSTRAR ERROR SI EXISTE */}
      {error && (
        <div
          style={{
            background: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
            border: "1px solid #fecaca",
            borderRadius: "12px",
            padding: "16px",
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              width: "24px",
              height: "24px",
              background: "#ef4444",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <AlertTriangle size={16} color="white" />
          </div>
          <div style={{ flex: 1 }}>
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                fontWeight: "600",
                color: "#dc2626",
              }}
            >
              Error de configuración
            </p>
            <p
              style={{
                margin: "4px 0 0 0",
                fontSize: "13px",
                color: "#991b1b",
              }}
            >
              {error}
            </p>
          </div>
          <button
            onClick={() => setError(null)}
            style={{
              background: "none",
              border: "none",
              color: "#dc2626",
              cursor: "pointer",
              padding: "4px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* ✅ STATS COMPACTAS SIGUIENDO LOS LINEAMIENTOS - SOLO DESKTOP */}
      {!isMobile && (
        <div
          className="stats-section"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "12px",
            marginBottom: "20px",
            animation: "fadeInUp 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {[
            {
              label: "Total Proyectos",
              value: proyectos.length,
              icon: Building2,
              color: "#3b82f6",
            },
            {
              label: "Activos",
              value: proyectos.filter((p) => !p.obra_finalizada).length,
              icon: Activity,
              color: "#10b981",
            },
            {
              label: "Finalizados",
              value: proyectos.filter((p) => p.obra_finalizada).length,
              icon: CheckCircle2,
              color: "#f59e0b",
            },
            {
              label: "Este Mes",
              value: proyectos.filter((p) => {
                const createdDate = new Date(p.created_at);
                const now = new Date();
                return (
                  createdDate.getMonth() === now.getMonth() &&
                  createdDate.getFullYear() === now.getFullYear()
                );
              }).length,
              icon: Calendar,
              color: "#8b5cf6",
            },
          ].map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={index}
                className="stat-card"
                style={{
                  background: "white",
                  padding: "12px",
                  borderRadius: "12px",
                  textAlign: "center",
                  border: "1px solid #f1f5f9",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  minHeight: "80px",
                  animation: `slideInRight 0.${
                    8 + index
                  }s cubic-bezier(0.4, 0, 0.2, 1)`,
                }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    background: `${stat.color}10`,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 8px",
                  }}
                >
                  <IconComponent size={16} color={stat.color} />
                </div>
                <p
                  style={{
                    fontSize: "10px",
                    color: "#64748b",
                    margin: "0 0 4px 0",
                    fontWeight: "500",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {stat.label}
                </p>
                <p
                  style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    color: "#1e293b",
                    margin: 0,
                  }}
                >
                  {stat.value}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* ✅ CONTENIDO PRINCIPAL */}
      {loading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "80px 20px",
            background: "white",
            borderRadius: "16px",
            border: "1px solid #f1f5f9",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                border: "2px solid #e2e8f0",
                borderTop: "2px solid #3b82f6",
                borderRadius: "50%",
                margin: "0 auto 16px",
                animation: "spin 1s linear infinite",
              }}
            />
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#64748b",
                fontWeight: "500",
              }}
            >
              Cargando...
            </p>
          </div>
        </div>
      ) : filteredProyectos.length === 0 ? (
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: isMobile ? "40px 20px" : "60px 40px",
            textAlign: "center",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            border: "1px solid #f1f5f9",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              background: colorVista.gradient,
              borderRadius: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
            }}
          >
            <Building2 size={32} color="white" />
          </div>
          <h3
            style={{
              fontSize: isMobile ? "18px" : "20px",
              color: "#1e293b",
              marginBottom: "10px",
              fontWeight: "600",
            }}
          >
            {searchTerm || filterStatus !== "todos"
              ? "No se encontraron proyectos"
              : "No hay proyectos aún"}
          </h3>
          <p
            style={{
              color: "#64748b",
              fontSize: "14px",
              marginBottom:
                searchTerm || filterStatus !== "todos" ? "0" : "25px",
              lineHeight: "1.5",
            }}
          >
            {searchTerm || filterStatus !== "todos"
              ? "Intenta cambiar los filtros de búsqueda"
              : "Comienza creando tu primer proyecto"}
          </p>
          {!searchTerm && filterStatus === "todos" && (
            <button
              onClick={() => {
                setEditingProject(null);
                setShowModal(true);
              }}
              className="button-hover"
              style={{
                background: colorVista.gradient,
                color: "white",
                border: "none",
                padding: "14px 28px",
                borderRadius: "12px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
            >
              Crear Primer Proyecto
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Vista móvil - Cards */}
          {isMobile ? (
            <div style={{ padding: "8px" }}>
              {filteredProyectos.map((proyecto, index) => (
                <ProyectoCardMobile
                  key={proyecto.id}
                  proyecto={proyecto}
                  index={index}
                  onEditar={() => {
                    setEditingProject(proyecto);
                    setShowModal(true);
                  }}
                  onEliminar={() => handleEliminar(proyecto.id)}
                  onRestaurar={() => handleRestaurar(proyecto.id)}
                  onToggleEstado={() =>
                    toggleObraFinalizada(proyecto.id, proyecto.obra_finalizada)
                  }
                  isEliminadosView={filterStatus === 'eliminados'}
                />
              ))}
            </div>
          ) : (
            /* Vista desktop - Grid de cards */
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
                gap: "20px",
                animation: "fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              {filteredProyectos.map((proyecto, index) => (
                <ProyectoCard
                  key={proyecto.id}
                  proyecto={proyecto}
                  index={index}
                  onEditar={() => {
                    setEditingProject(proyecto);
                    setShowModal(true);
                  }}
                  onEliminar={() => handleEliminar(proyecto.id)}
                  onRestaurar={() => handleRestaurar(proyecto.id)}
                  onToggleEstado={() =>
                    toggleObraFinalizada(proyecto.id, proyecto.obra_finalizada)
                  }
                  isEliminadosView={filterStatus === 'eliminados'}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* ✅ BOTÓN FLOTANTE MÓVIL UNIFICADO */}
      {isMobile && (
        <div
          style={{
            position: "fixed",
            bottom: "100px",
            right: "20px",
            zIndex: 1000,
          }}
        >
          <button
            onClick={() => {
              setEditingProject(null);
              setShowModal(true);
            }}
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "28px",
              background: colorVista.gradient,
              color: "white",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              boxShadow: `0 8px 32px ${colorVista.light}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
            onTouchStart={(e) => {
              e.currentTarget.style.transform = "scale(0.95)";
            }}
            onTouchEnd={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <Plus size={24} />
          </button>
        </div>
      )}

      {/* Modal del Wizard */}
      {showModal && (
        <ProyectoWizard
          constructora={constructora}
          proyecto={editingProject}
          proyectosExistentes={proyectos}
          onClose={() => {
            setShowModal(false);
            setEditingProject(null);
          }}
          onSave={() => {
            setShowModal(false);
            setEditingProject(null);
            cargarProyectos();
          }}
          isMobile={isMobile}
          showCustomAlert={showCustomAlert}
        />
      )}

      {/* Modal personalizado para alertas */}
      <CustomModal
        isOpen={customModal.isOpen}
        onClose={() => setCustomModal((prev) => ({ ...prev, isOpen: false }))}
        type={customModal.type}
        title={customModal.title}
        message={customModal.message}
        onConfirm={customModal.onConfirm}
        options={customModal.options}
        confirmText={customModal.confirmText}
        isMobile={isMobile}
      />

      {/* ✅ CSS OBLIGATORIO - ANIMACIONES UNIFICADAS */}
      <style jsx>{`
        /* Configuración anti-zoom */
        input[type="text"],
        input[type="number"],
        input[type="date"],
        input[type="email"],
        input[type="tel"],
        input[type="search"],
        textarea,
        select {
          font-size: 16px !important;
          touch-action: manipulation;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        input:focus,
        textarea:focus,
        select:focus {
          font-size: 16px !important;
          outline: none;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        @media (max-width: 768px) {
          input,
          textarea,
          select,
          button {
            font-size: 16px !important;
            -webkit-appearance: none !important;
            appearance: none !important;
          }
        }

        /* Animaciones estándar */
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .loading-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        /* Hover effects */
        .button-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .action-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        /* Responsive desktop crítico */
        @media (min-width: 769px) {
          .header-section > div:first-child {
            flex-wrap: nowrap !important;
            min-height: 56px;
          }

          .button-hover {
            flex-shrink: 0 !important;
            white-space: nowrap !important;
            min-width: max-content !important;
          }

          .main-container {
            min-width: 1024px;
          }
        }
      `}</style>
    </div>
  );
}

// ✅ CARD MÓVIL UNIFICADA SIGUIENDO LOS LINEAMIENTOS
function ProyectoCardMobile({
  proyecto,
  index,
  onEditar,
  onEliminar,
  onRestaurar,
  onToggleEstado,
  isEliminadosView = false,
}) {
  // Calcular días desde inicio
  const calcularDiasProyecto = () => {
    if (!proyecto.fecha_inicio) return null;

    const fechaInicio = new Date(proyecto.fecha_inicio);
    const fechaFin =
      proyecto.obra_finalizada && proyecto.fecha_finalizacion
        ? new Date(proyecto.fecha_finalizacion)
        : new Date();

    const diffTime = fechaFin - fechaInicio;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      dias: diffDays,
      finalizada: proyecto.obra_finalizada,
    };
  };

  const tiempoProyecto = calcularDiasProyecto();

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #f1f5f9",
        borderRadius: "12px",
        padding: "16px",
        marginBottom: "8px",
        cursor: "pointer",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        animation: `slideInRight 0.${
          5 + (index % 10)
        }s cubic-bezier(0.4, 0, 0.2, 1)`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        touchAction: "manipulation",
      }}
      onTouchStart={(e) => {
        e.currentTarget.style.transform = "scale(0.98)";
        e.currentTarget.style.backgroundColor = "#f8fafc";
      }}
      onTouchEnd={(e) => {
        e.currentTarget.style.transform = "scale(1)";
        e.currentTarget.style.backgroundColor = "white";
      }}
    >
      {/* HEADER UNIFICADO */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "12px",
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "700",
              color: "#1e293b",
              marginBottom: "4px",
            }}
          >
            {proyecto.codigo}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "#64748b",
              fontWeight: "500",
            }}
          >
            Cliente: {proyecto.apellido_cliente}
          </div>
        </div>
        <div
          style={{
            textAlign: "right",
            marginLeft: "12px",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "4px",
              marginBottom: "4px",
            }}
          >
            <Calendar size={10} />
            {tiempoProyecto ? `${tiempoProyecto.dias} días` : "Sin fecha"}
          </div>
          <div
            style={{
              fontSize: "11px",
              fontWeight: "600",
              color: proyecto.obra_finalizada ? "#10b981" : "#f59e0b",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "4px",
            }}
          >
            {proyecto.obra_finalizada ? (
              <CheckCircle2 size={10} />
            ) : (
              <Clock size={10} />
            )}
            {proyecto.obra_finalizada ? "Finalizada" : "En progreso"}
          </div>
        </div>
      </div>

      {/* BADGES UNIFICADOS */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          marginBottom: "12px",
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            background:
              proyecto.tipo_obra === "llave_en_mano" ? "#fef3cd" : "#e0f2fe",
            color:
              proyecto.tipo_obra === "llave_en_mano" ? "#92400e" : "#0c4a6e",
            padding: "4px 8px",
            borderRadius: "6px",
            fontSize: "11px",
            fontWeight: "600",
          }}
        >
          {proyecto.tipo_obra === "llave_en_mano"
            ? "Llave en Mano"
            : "Administración"}
        </span>

        {proyecto.mostrar_cajas && (
          <span
            style={{
              fontSize: "9px",
              background: "#f0fdf4",
              color: "#15803d",
              padding: "2px 6px",
              borderRadius: "4px",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              gap: "2px",
            }}
          >
            <DollarSign size={8} />
            Cajas
          </span>
        )}

        {proyecto.mano_obra_separada && (
          <span
            style={{
              fontSize: "9px",
              background: "#ddd6fe",
              color: "#5b21b6",
              padding: "2px 6px",
              borderRadius: "4px",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              gap: "2px",
            }}
          >
            <User size={8} />
            M.O.
          </span>
        )}
      </div>

      {/* DESCRIPCIÓN */}
      {proyecto.ubicacion && (
        <div
          style={{
            fontSize: "12px",
            color: "#64748b",
            marginBottom: "12px",
            lineHeight: "1.4",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <MapPin size={12} />
          {proyecto.ubicacion}
        </div>
      )}

      {/* MÉTRICAS */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "8px",
          marginBottom: "12px",
        }}
      >
        <div
          style={{
            background: "#f8fafc",
            padding: "8px",
            borderRadius: "6px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontWeight: "700",
              color: "#1e293b",
            }}
          >
            {proyecto.metros_cubiertos || "0"}
          </div>
          <div
            style={{
              fontSize: "9px",
              color: "#64748b",
            }}
          >
            m² cubier.
          </div>
        </div>

        <div
          style={{
            background: "#f8fafc",
            padding: "8px",
            borderRadius: "6px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontWeight: "700",
              color: "#1e293b",
            }}
          >
            {proyecto.metros_computo || "0"}
          </div>
          <div
            style={{
              fontSize: "9px",
              color: "#64748b",
            }}
          >
            m² cómp.
          </div>
        </div>

        <div
          style={{
            background: "#f8fafc",
            padding: "8px",
            borderRadius: "6px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontWeight: "700",
              color: "#10b981",
            }}
          >
            {proyecto.monto_total_llave_en_mano
              ? `${proyecto.moneda_llave_en_mano} ${Number(
                  proyecto.monto_total_llave_en_mano
                ).toLocaleString()}`
              : proyecto.honorarios
              ? `${proyecto.moneda_honorarios} ${Number(
                  proyecto.honorarios
                ).toLocaleString()}`
              : "-"}
          </div>
          <div
            style={{
              fontSize: "9px",
              color: "#64748b",
            }}
          >
            monto
          </div>
        </div>
      </div>

      {/* FOOTER UNIFICADO */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: "12px",
          borderTop: "1px solid #f1f5f9",
        }}
      >
        <div style={{ display: "flex", gap: "4px" }}>
          {/* Toggle estado */}
          {!isEliminadosView && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleEstado();
            }}
            style={{
              background: proyecto.obra_finalizada ? "#dcfce7" : "#f1f5f9",
              border: "none",
              borderRadius: "6px",
              padding: "6px 8px",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: "600",
              color: proyecto.obra_finalizada ? "#166534" : "#64748b",
              touchAction: "manipulation",
            }}
          >
            {proyecto.obra_finalizada ? (
              <CheckCircle2 size={12} />
            ) : (
              <Clock size={12} />
            )}
          </button>
          )}
        </div>

        <div style={{ display: "flex", gap: "4px" }}>
          {!isEliminadosView && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEditar();
            }}
            style={{
              background: "#eff6ff",
              border: "none",
              borderRadius: "6px",
              padding: "6px 8px",
              cursor: "pointer",
              color: "#3b82f6",
              touchAction: "manipulation",
            }}
          >
            <Edit size={12} />
          </button>
          )}
          {isEliminadosView ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRestaurar();
              }}
              style={{
                background: "#ecfdf5",
                border: "none",
                borderRadius: "6px",
                padding: "6px 8px",
                cursor: "pointer",
                color: "#16a34a",
                touchAction: "manipulation",
              }}
            >
              <RotateCcw size={12} />
            </button>
          ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEliminar();
            }}
            style={{
              background: "#fef2f2",
              border: "none",
              borderRadius: "6px",
              padding: "6px 8px",
              cursor: "pointer",
              color: "#ef4444",
              touchAction: "manipulation",
            }}
          >
            <Trash2 size={12} />
          </button>)}
        </div>
      </div>
    </div>
  );
}

// ✅ CARD DESKTOP SIGUIENDO LOS LINEAMIENTOS
function ProyectoCard({
  proyecto,
  index,
  onEditar,
  onEliminar,
  onRestaurar,
  onToggleEstado,
  isEliminadosView = false,
}) {
  // Calcular días desde inicio
  const calcularDiasProyecto = () => {
    if (!proyecto.fecha_inicio) return null;

    const fechaInicio = new Date(proyecto.fecha_inicio);
    const fechaFin =
      proyecto.obra_finalizada && proyecto.fecha_finalizacion
        ? new Date(proyecto.fecha_finalizacion)
        : new Date();

    const diffTime = fechaFin - fechaInicio;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      dias: diffDays,
      finalizada: proyecto.obra_finalizada,
    };
  };

  const tiempoProyecto = calcularDiasProyecto();

  return (
    <div
      className="project-card"
      style={{
        background: "white",
        borderRadius: "16px",
        padding: "24px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
        border: "1px solid #f1f5f9",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative",
        overflow: "hidden",
        animation: `slideInRight 0.${
          5 + (index % 10)
        }s cubic-bezier(0.4, 0, 0.2, 1)`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.12)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
      }}
    >
      {/* Barra de estado superior */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: proyecto.obra_finalizada
            ? "linear-gradient(90deg, #10b981, #059669)"
            : "linear-gradient(90deg, #3b82f6, #2563eb)",
        }}
      ></div>

      {/* Header del card */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "16px",
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "8px",
              flexWrap: "wrap",
            }}
          >
            <h3
              style={{
                fontSize: "20px",
                fontWeight: "700",
                color: "#1e293b",
                margin: 0,
              }}
            >
              {proyecto.codigo}
            </h3>
            <span
              style={{
                background: proyecto.obra_finalizada ? "#dcfce7" : "#dbeafe",
                color: proyecto.obra_finalizada ? "#166534" : "#1d4ed8",
                padding: "4px 10px",
                borderRadius: "20px",
                fontSize: "11px",
                fontWeight: "600",
              }}
            >
              {proyecto.obra_finalizada ? "Finalizada" : "Activa"}
            </span>
            {proyecto.mostrar_cajas && (
              <span
                style={{
                  background: "#f0fdf4",
                  color: "#15803d",
                  padding: "4px 8px",
                  borderRadius: "15px",
                  fontSize: "10px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <DollarSign size={12} />
                Cajas
              </span>
            )}
          </div>

          <p
            style={{
              fontSize: "15px",
              color: "#64748b",
              margin: "0 0 4px 0",
            }}
          >
            Cliente: {proyecto.apellido_cliente}
          </p>

          {proyecto.ubicacion && (
            <p
              style={{
                fontSize: "13px",
                color: "#94a3b8",
                margin: "0 0 4px 0",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <MapPin size={12} />
              {proyecto.ubicacion}
            </p>
          )}

          {/* Información de tiempo */}
          {tiempoProyecto && (
            <p
              style={{
                fontSize: "13px",
                color: proyecto.obra_finalizada ? "#059669" : "#0ea5e9",
                margin: "4px 0 0 0",
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <Calendar size={12} />
              {tiempoProyecto.dias} días{" "}
              {tiempoProyecto.finalizada ? "completados" : "en curso"}
              {proyecto.fecha_inicio && (
                <span style={{ color: "#94a3b8", fontWeight: "400" }}>
                  {" • Inicio: " +
                    new Date(proyecto.fecha_inicio).toLocaleDateString()}
                </span>
              )}
            </p>
          )}
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            marginLeft: "15px",
          }}
        >
          {/* Toggle obra finalizada */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleEstado();
            }}
            className="action-button"
            style={{
              background: proyecto.obra_finalizada ? "#dcfce7" : "#f1f5f9",
              border: "none",
              borderRadius: "8px",
              padding: "8px 10px",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: "600",
              color: proyecto.obra_finalizada ? "#166534" : "#64748b",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {proyecto.obra_finalizada ? (
              <CheckCircle2 size={14} />
            ) : (
              <Clock size={14} />
            )}
            {proyecto.obra_finalizada ? "Finalizada" : "En progreso"}
          </button>

          <div
            style={{
              display: "flex",
              gap: "6px",
            }}
          >
            {!isEliminadosView && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEditar();
              }}
              className="action-button"
              style={{
                background: "#f1f5f9",
                border: "none",
                borderRadius: "8px",
                padding: "8px 10px",
                cursor: "pointer",
                color: "#475569",
                fontSize: "14px",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <Edit size={14} />
            </button>
            )}
            {!isEliminadosView && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEliminar();
              }}
              className="action-button"
              style={{
                background: "#fef2f2",
                border: "none",
                borderRadius: "8px",
                padding: "8px 10px",
                cursor: "pointer",
                color: "#dc2626",
                fontSize: "14px",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <Trash2 size={14} />
            </button>
            )}
            {isEliminadosView && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRestaurar();
              }}
              className="action-button"
              style={{
                background: "#ecfdf5",
                border: "none",
                borderRadius: "8px",
                padding: "8px 10px",
                cursor: "pointer",
                color: "#16a34a",
                fontSize: "14px",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <RotateCcw size={14} />
            </button>
            )}
          </div>
        </div>
      </div>

      {/* Métricas */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        <div
          style={{
            background: "#f8fafc",
            padding: "12px",
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          <p
            style={{
              fontSize: "11px",
              color: "#64748b",
              margin: "0 0 4px 0",
              fontWeight: "500",
            }}
          >
            M² CUBIERTOS
          </p>
          <p
            style={{
              fontSize: "16px",
              fontWeight: "700",
              color: "#1e293b",
              margin: 0,
            }}
          >
            {proyecto.metros_cubiertos || "0"}
          </p>
        </div>

        <div
          style={{
            background: "#f8fafc",
            padding: "12px",
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          <p
            style={{
              fontSize: "11px",
              color: "#64748b",
              margin: "0 0 4px 0",
              fontWeight: "500",
            }}
          >
            M² CÓMPUTO
          </p>
          <p
            style={{
              fontSize: "16px",
              fontWeight: "700",
              color: "#1e293b",
              margin: 0,
            }}
          >
            {proyecto.metros_computo || "0"}
          </p>
        </div>
      </div>

      {/* Tipo de obra y monto */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: "16px",
          borderTop: "1px solid #f1f5f9",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <span
          style={{
            background:
              proyecto.tipo_obra === "llave_en_mano" ? "#fef3cd" : "#e0f2fe",
            color:
              proyecto.tipo_obra === "llave_en_mano" ? "#92400e" : "#0c4a6e",
            padding: "6px 12px",
            borderRadius: "20px",
            fontSize: "12px",
            fontWeight: "600",
          }}
        >
          {proyecto.tipo_obra === "llave_en_mano"
            ? "Llave en Mano"
            : "Administración"}
        </span>

        {proyecto.monto_total_llave_en_mano && (
          <span
            style={{
              fontSize: "14px",
              fontWeight: "700",
              color: "#059669",
            }}
          >
            {proyecto.moneda_llave_en_mano}{" "}
            {Number(proyecto.monto_total_llave_en_mano).toLocaleString()}
          </span>
        )}

        {proyecto.honorarios && (
          <div style={{ color: "#075985" }}>
            <strong>Honorarios:</strong> {proyecto.moneda_honorarios} {Number(proyecto.honorarios).toLocaleString()}
            {proyecto.honorarios_se_actualizan &&
              ` (${proyecto.metodo_actualizacion_honorarios})`}
          </div>
        )}
      </div>

      {/* Información de cajas creadas */}
      {proyecto.mostrar_cajas && (
        <div
          style={{
            marginTop: "12px",
            padding: "12px",
            background: "#f0fdf4",
            borderRadius: "8px",
            border: "1px solid #bbf7d0",
          }}
        >
          <p
            style={{
              fontSize: "12px",
              color: "#15803d",
              margin: "0 0 4px 0",
              fontWeight: "600",
            }}
          >
            Cajas creadas automáticamente:
          </p>
          <div
            style={{
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontSize: "11px",
                background: "#dcfce7",
                color: "#166534",
                padding: "2px 6px",
                borderRadius: "12px",
                fontWeight: "500",
              }}
            >
              Proyecto ARS/USD
            </span>
            {proyecto.mano_obra_separada && (
              <span
                style={{
                  fontSize: "11px",
                  background: "#ddd6fe",
                  color: "#5b21b6",
                  padding: "2px 6px",
                  borderRadius: "12px",
                  fontWeight: "500",
                }}
              >
                Mano Obra ARS/USD
              </span>
            )}
          </div>
        </div>
      )}

      {/* Render preview */}
      {proyecto.render_url && (
        <div
          style={{
            marginTop: "16px",
            borderRadius: "10px",
            overflow: "hidden",
            height: "120px",
            background: "#f8fafc",
            position: "relative",
          }}
        >
          <img
            src={proyecto.render_url}
            alt="Render del proyecto"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              transition: "transform 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
            }}
            onError={(e) => {
              e.target.parentElement.innerHTML = `
                <div style="
                  display: flex; 
                  align-items: center; 
                  justify-content: center; 
                  height: 100%; 
                  color: #64748b; 
                  font-size: 12px;
                  text-align: center;
                ">
                  <div>
                    <div style="font-size: 24px; margin-bottom: 4px;">🖼️</div>
                    Error cargando render
                  </div>
                </div>
              `;
            }}
          />
        </div>
      )}
    </div>
  );
}

// ✅ COMPONENTE WIZARD MEJORADO SIGUIENDO LOS LINEAMIENTOS
function ProyectoWizard({
  constructora,
  proyecto,
  proyectosExistentes,
  onClose,
  onSave,
  isMobile,
  showCustomAlert,
}) {
  // Body scroll es manejado por el componente padre `Proyectos`

  // Función helper para formatear números con separadores de miles
  const formatearNumero = (valor) => {
    if (!valor) return "";

    // Convertir coma decimal a punto para procesamiento
    let numero = valor.toString().replace(",", ".");

    // Separar parte entera y decimal
    const partes = numero.split(".");
    let parteEntera = partes[0].replace(/\D/g, "");
    const parteDecimal = partes[1] || "";

    // Agregar puntos cada 3 dígitos en la parte entera
    parteEntera = parteEntera.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

    // Reconstruir con coma decimal si hay decimales
    return parteDecimal ? `${parteEntera},${parteDecimal}` : parteEntera;
  };

  // Función para limpiar formato y convertir a número
  const limpiarFormato = (valor) => {
    if (!valor) return "";
    // Convertir coma decimal a punto y remover puntos de miles
    return valor.replace(/\./g, "").replace(",", ".");
  };

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    codigo: "",
    apellido_cliente: "",
    email_cliente: "",
    cuit_cliente: "",
    ubicacion: "",
    metros_cubiertos: "",
    metros_semicubiertos: "",
    metros_solados: "",
    metros_computo: "",
    tipo_obra: "llave_en_mano",

    // Llave en mano
    monto_total_llave_en_mano: "",
    moneda_llave_en_mano: "ARS",
    metodo_actualizacion: "",
    nombre_metodo_personalizado: "",
    fecha_inicio_actualizacion_llave_en_mano: "",

    // Honorarios
    honorarios: "",
    porcentaje_honorario: "", // <-- nuevo campo para porcentaje
    tipo_monto_honorario: "$", // <-- nuevo campo para tipo de monto
    moneda_honorarios: "ARS",
    honorarios_se_actualizan: false,
    metodo_actualizacion_honorarios: "",
    nombre_metodo_personalizado_honorarios: "",
    fecha_inicio_actualizacion_honorarios: "",

    // Mano de obra
    mano_obra_separada: false,
    monto_mano_obra: "",
    moneda_mano_obra: "ARS",
    mano_obra_se_actualiza: false,
    metodo_actualizacion_mano_obra: "",
    nombre_metodo_personalizado_mano_obra: "",
    fecha_inicio_actualizacion_mano_obra: "",

    fecha_inicio: "",
    fecha_finalizacion: "",
    obra_finalizada: false,
    mostrar_cajas: true,
    render_url: "",
    tipo_cambio: "",

    // Adicionales
    adicionales_honorarios_habilitado: false,
    adicionales_tipo_honorarios: "porcentaje",
    adicionales_porcentaje_honorarios: "",
    adicionales_monto_fijo_honorarios: "",
    adicionales_moneda_honorarios: "USD",
    adicionales_actualizar_con_indice: false,
    adicionales_metodo_actualizacion: "CAC_GENERAL",
    adicionales_nombre_metodo_personalizado: "",
    adicionales_fecha_inicio_actualizacion: "",
  });

  // Tooltip para explicar por qué no se puede habilitar adicionales con %
  const [showTooltipAdic, setShowTooltipAdic] = useState(false);

  // Bloquear y resetear honorarios por adicionales si los honorarios generales son %
  useEffect(() => {
    const esPorcentaje =
      formData?.tipo_obra === "administracion_porcentaje" ||
      (formData?.tipo_obra === "administracion" && formData?.tipo_monto_honorario === "%");
    if (esPorcentaje && formData?.adicionales_honorarios_habilitado) {
      setFormData((prev) => ({
        ...prev,
        adicionales_honorarios_habilitado: false,
        adicionales_tipo_honorarios: null,
        adicionales_porcentaje_honorarios: null,
        adicionales_monto_fijo_honorarios: null,
        adicionales_moneda_honorarios: null,
        adicionales_actualizar_con_indice: false,
        adicionales_metodo_actualizacion: null,
        adicionales_fecha_inicio_actualizacion: null,
      }));
    }
  }, [formData?.tipo_obra, formData?.tipo_monto_honorario]);

  const [guardando, setGuardando] = useState(false);
  const [subiendoRender, setSubiendoRender] = useState(false);

  const colorVista = getVistaColor();

  useEffect(() => {
    if (proyecto) {
      // Función helper para detectar si es un método personalizado
      const esMetodoPersonalizado = (metodo) => {
        if (!metodo) return false;
        const metodosEstandar = [
          "CAC_GENERAL",
          "CAC_MATERIALES",
          "CAC_MANO_OBRA",
        ];
        return !metodosEstandar.includes(metodo);
      };

      // Detectar tipo de monto para administración
      let tipo_monto_honorario = "$";
      let honorarios = "";
      let porcentaje_honorario = "";
      let tipo_obra = proyecto.tipo_obra;
      if (proyecto.tipo_obra === "administracion_porcentaje") {
        tipo_monto_honorario = "%";
        porcentaje_honorario = proyecto.porcentaje_honorario ? String(proyecto.porcentaje_honorario) : "";
      } else if (proyecto.tipo_obra === "administracion") {
        // Si tiene porcentaje_honorario, es porcentaje
        if (proyecto.porcentaje_honorario) {
          tipo_monto_honorario = "%";
          porcentaje_honorario = String(proyecto.porcentaje_honorario);
        } else {
          tipo_monto_honorario = "$";
          honorarios = proyecto.honorarios ? String(proyecto.honorarios) : "";
        }
      }

      console.log("🔍 DEBUG EDITAR PROYECTO:", {
        tipo_obra: proyecto.tipo_obra,
        porcentaje_honorario: proyecto.porcentaje_honorario,
        honorarios: proyecto.honorarios,
        tipo_monto_honorario_detectado: tipo_monto_honorario,
        moneda_honorarios: proyecto.moneda_honorarios
      });

      setFormData({
        codigo: proyecto.codigo || "",
        apellido_cliente: proyecto.apellido_cliente || "",
        email_cliente: proyecto.email_cliente || "",
        cuit_cliente: proyecto.cuit_cliente || "",
        ubicacion: proyecto.ubicacion || "",
        metros_cubiertos: proyecto.metros_cubiertos || "",
        metros_semicubiertos: proyecto.metros_semicubiertos || "",
        metros_solados: proyecto.metros_solados || "",
        metros_computo: proyecto.metros_computo || "",
        tipo_obra: proyecto.tipo_obra || "llave_en_mano",

        // Llave en mano
        monto_total_llave_en_mano: proyecto.monto_total_llave_en_mano ? String(proyecto.monto_total_llave_en_mano) : "",
        moneda_llave_en_mano: proyecto.moneda_llave_en_mano || "ARS",
        metodo_actualizacion: esMetodoPersonalizado(
          proyecto.metodo_actualizacion
        )
          ? "PERSONALIZADO"
          : proyecto.metodo_actualizacion || "",
        nombre_metodo_personalizado: esMetodoPersonalizado(
          proyecto.metodo_actualizacion
        )
          ? proyecto.metodo_actualizacion
          : "",
        fecha_inicio_actualizacion_llave_en_mano:
          proyecto.fecha_inicio_actualizacion_llave_en_mano || "",

        // Honorarios
        honorarios,
        porcentaje_honorario,
        tipo_monto_honorario,
        moneda_honorarios: proyecto.moneda_honorarios || "ARS",
        honorarios_se_actualizan: proyecto.honorarios_se_actualizan || false,
        metodo_actualizacion_honorarios: esMetodoPersonalizado(
          proyecto.metodo_actualizacion_honorarios
        )
          ? "PERSONALIZADO"
          : proyecto.metodo_actualizacion_honorarios || "",
        nombre_metodo_personalizado_honorarios: esMetodoPersonalizado(
          proyecto.metodo_actualizacion_honorarios
        )
          ? proyecto.metodo_actualizacion_honorarios
          : "",
        fecha_inicio_actualizacion_honorarios:
          proyecto.fecha_inicio_actualizacion_honorarios || "",

        // Mano de obra
        mano_obra_separada: proyecto.mano_obra_separada || false,
        monto_mano_obra: proyecto.monto_mano_obra ? String(proyecto.monto_mano_obra) : "",
        moneda_mano_obra: proyecto.moneda_mano_obra || "ARS",
        mano_obra_se_actualiza: proyecto.mano_obra_se_actualiza || false,
        metodo_actualizacion_mano_obra: esMetodoPersonalizado(
          proyecto.metodo_actualizacion_mano_obra
        )
          ? "PERSONALIZADO"
          : proyecto.metodo_actualizacion_mano_obra || "",
        nombre_metodo_personalizado_mano_obra: esMetodoPersonalizado(
          proyecto.metodo_actualizacion_mano_obra
        )
          ? proyecto.metodo_actualizacion_mano_obra
          : "",
        fecha_inicio_actualizacion_mano_obra:
          proyecto.fecha_inicio_actualizacion_mano_obra || "",

        fecha_inicio: proyecto.fecha_inicio || "",
        fecha_finalizacion: proyecto.fecha_finalizacion || "",
        obra_finalizada: proyecto.obra_finalizada || false,
        mostrar_cajas: proyecto.mostrar_cajas !== false,
        render_url: proyecto.render_url || "",
        tipo_cambio: proyecto.tipo_cambio || "",

        // Adicionales
        adicionales_honorarios_habilitado: proyecto.adicionales_honorarios_habilitado || false,
        adicionales_tipo_honorarios: proyecto.adicionales_tipo_honorarios || "porcentaje",
        adicionales_porcentaje_honorarios: proyecto.adicionales_porcentaje_honorarios ? String(proyecto.adicionales_porcentaje_honorarios) : "",
        adicionales_monto_fijo_honorarios: proyecto.adicionales_monto_fijo_honorarios ? String(proyecto.adicionales_monto_fijo_honorarios) : "",
        adicionales_moneda_honorarios: proyecto.adicionales_moneda_honorarios || "USD",
        adicionales_actualizar_con_indice: proyecto.adicionales_actualizar_con_indice || false,
        adicionales_metodo_actualizacion: esMetodoPersonalizado(proyecto.adicionales_metodo_actualizacion) ? "PERSONALIZADO" : proyecto.adicionales_metodo_actualizacion || "CAC_GENERAL",
        adicionales_nombre_metodo_personalizado: esMetodoPersonalizado(proyecto.adicionales_metodo_actualizacion) ? proyecto.adicionales_metodo_actualizacion : "",
        adicionales_fecha_inicio_actualizacion: proyecto.adicionales_fecha_inicio_actualizacion || "",
      });
    }
  }, [proyecto]);

  // Generar código automáticamente desde apellido
  const generarCodigo = (apellido) => {
    if (!apellido || apellido.length < 3) return "";

    const baseCode = apellido.substring(0, 3).toUpperCase();

    // Verificar si existe el código base
    const codigosExistentes = proyectosExistentes
      .filter((p) => (proyecto ? p.id !== proyecto.id : true))
      .map((p) => p.codigo.toUpperCase());

    if (!codigosExistentes.includes(baseCode)) {
      return baseCode;
    }

    // Si existe, buscar el siguiente número disponible
    let counter = 1;
    let newCode;
    do {
      newCode = `${baseCode}${counter}`;
      counter++;
    } while (codigosExistentes.includes(newCode));

    return newCode;
  };

  // Actualizar código cuando cambia el apellido
  useEffect(() => {
    if (!proyecto && formData.apellido_cliente) {
      const nuevoCodigo = generarCodigo(formData.apellido_cliente);
      setFormData((prev) => ({
        ...prev,
        codigo: nuevoCodigo,
      }));
    }
  }, [formData.apellido_cliente, proyecto, proyectosExistentes]);

  // Calcular metros de cómputo automáticamente
  useEffect(() => {
    const cubiertos = parseFloat(formData.metros_cubiertos) || 0;
    const semicubiertos = parseFloat(formData.metros_semicubiertos) || 0;
    const solados = parseFloat(formData.metros_solados) || 0;

    const computo = cubiertos * 1.0 + semicubiertos * 0.5 + solados * 0.25;

    setFormData((prev) => ({
      ...prev,
      metros_computo: computo.toFixed(2),
    }));
  }, [
    formData.metros_cubiertos,
    formData.metros_semicubiertos,
    formData.metros_solados,
  ]);

  // Nuevo useEffect para traer el dólar oficial venta si es nuevo proyecto
  useEffect(() => {
    if (!proyecto) {
      fetch("https://dolarapi.com/v1/dolares/oficial")
        .then((res) => res.json())
        .then((data) => {
          if (data && data.venta) {
            setFormData((prev) => ({ ...prev, tipo_cambio: data.venta }));
          }
        })
        .catch((err) => console.error("Error obteniendo dólar oficial:", err));
    }
  }, [proyecto]);

  const steps = [
    { id: 1, title: "Datos Básicos", icon: FileText },
    { id: 2, title: "Métricas", icon: Ruler },
    { id: 3, title: "Financiero", icon: DollarSign },
    { id: 4, title: "Render & Final", icon: Upload },
  ];

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return (
          formData.apellido_cliente !== "" &&
          formData.email_cliente !== "" &&
          formData.codigo !== ""
        );
      case 2:
        return formData.metros_cubiertos !== "";
      case 3:
        if (formData.tipo_obra === "llave_en_mano") {
          return formData.monto_total_llave_en_mano !== "";
        } else {
          if (formData.tipo_monto_honorario === "%") {
            return formData.porcentaje_honorario !== "";
          } else {
            return formData.honorarios !== "";
          }
        }
      case 4:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    } else {
      showCustomAlert(
        "error",
        "Campos requeridos",
        "Por favor completa todos los campos obligatorios antes de continuar"
      );
    }
  };

  const handleRenderUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Por favor selecciona un archivo de imagen");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert("El archivo es muy grande. Máximo 10MB");
      return;
    }

    setSubiendoRender(true);

    try {
      // Nombre único y limpio
      const fileExt = file.name.split(".").pop().toLowerCase();
      const timestamp = Date.now();
      const fileName = `proyecto-${constructora.id}-${timestamp}.${fileExt}`;

      console.log("Subiendo archivo:", fileName);

      // Subir con configuración específica
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("renders")
        .upload(fileName, file, {
          upsert: true,
          contentType: file.type,
          cacheControl: "3600",
        });

      if (uploadError) {
        console.error("Error en upload:", uploadError);
        throw uploadError;
      }

      console.log("Upload exitoso:", uploadData);

      // Obtener URL pública
      const { data: urlData } = supabase.storage
        .from("renders")
        .getPublicUrl(fileName);

      const finalUrl = urlData.publicUrl;
      console.log("URL generada:", finalUrl);

      // Verificar que la imagen sea accesible
      try {
        const response = await fetch(finalUrl, { method: "HEAD" });
        if (!response.ok) {
          throw new Error(`URL no accesible: ${response.status}`);
        }
        console.log("Imagen verificada como accesible");
      } catch (fetchError) {
        console.warn(
          "Advertencia - no se pudo verificar la imagen:",
          fetchError
        );
      }

      // Actualizar formulario
      setFormData((prev) => ({
        ...prev,
        render_url: finalUrl,
      }));

      console.log("Render actualizado en formulario:", finalUrl);
    } catch (err) {
      console.error("Error completo subiendo render:", err);
      alert("Error al subir render: " + err.message);
    }

    setSubiendoRender(false);
  };

  const handleSave = async () => {
    setGuardando(true);

    // ✅ Validar que constructora.id sea un UUID válido
    if (!constructora?.id || typeof constructora.id !== 'string' || constructora.id.length !== 36) {
      console.error("❌ Error: constructora.id no es un UUID válido:", {
        constructoraId: constructora?.id,
        type: typeof constructora?.id,
        length: constructora?.id?.length
      });
      showCustomAlert(
        "error",
        "Error de datos",
        "Error: ID de constructora inválido. Por favor, recarga la página."
      );
      setGuardando(false);
      return;
    }

    console.log("🔍 Datos para guardar:", {
      constructoraId: constructora.id,
      constructoraIdType: typeof constructora.id,
      constructoraIdLength: constructora.id.length
    });

    try {
      let tipoObraFinal = formData.tipo_obra;
      let honorariosFinal = null;
      let porcentajeHonorarioFinal = null;

      if (formData.tipo_obra === "administracion") {
        if (formData.tipo_monto_honorario === "%") {
          tipoObraFinal = "administracion_porcentaje";
          // Limpiar y guardar como número decimal
          let valorPorcentaje = formData.porcentaje_honorario
            ? parseFloat(limpiarFormato(formData.porcentaje_honorario))
            : null;
          if (isNaN(valorPorcentaje)) valorPorcentaje = null;
          porcentajeHonorarioFinal = valorPorcentaje;
          honorariosFinal = null;
        } else {
          tipoObraFinal = "administracion";
          honorariosFinal = formData.honorarios
            ? parseFloat(limpiarFormato(formData.honorarios))
            : null;
          porcentajeHonorarioFinal = null;
        }
      }

      const dataToSave = {
        constructora_id: constructora.id,
        codigo: formData.codigo,
        apellido_cliente: formData.apellido_cliente,
        cuit_cliente: formData.cuit_cliente || null,
        email_cliente: formData.email_cliente,
        ubicacion: formData.ubicacion || null,
        metros_cubiertos: parseFloat(formData.metros_cubiertos) || 0,
        metros_semicubiertos: parseFloat(formData.metros_semicubiertos) || 0,
        metros_solados: parseFloat(formData.metros_solados) || 0,
        metros_computo: parseFloat(formData.metros_computo) || 0,
        tipo_obra:
          formData.tipo_obra === "llave_en_mano"
            ? formData.tipo_obra
            : tipoObraFinal,

        // Llave en mano
        monto_total_llave_en_mano: formData.monto_total_llave_en_mano
          ? parseFloat(limpiarFormato(formData.monto_total_llave_en_mano))
          : null,
        moneda_llave_en_mano:
          formData.tipo_obra === "llave_en_mano"
            ? formData.moneda_llave_en_mano
            : null,
        metodo_actualizacion:
          formData.tipo_obra === "llave_en_mano"
            ? formData.metodo_actualizacion === "PERSONALIZADO"
              ? formData.nombre_metodo_personalizado
              : formData.metodo_actualizacion
            : null,
        fecha_inicio_actualizacion_llave_en_mano:
          formData.tipo_obra === "llave_en_mano"
            ? formData.fecha_inicio_actualizacion_llave_en_mano || null
            : null,

        // Honorarios y porcentaje
        honorarios:
          formData.tipo_obra === "llave_en_mano"
            ? null
            : honorariosFinal,
        porcentaje_honorario:
          formData.tipo_obra === "llave_en_mano"
            ? null
            : porcentajeHonorarioFinal,
        moneda_honorarios:
          formData.tipo_obra === "administracion"
            ? formData.moneda_honorarios
            : null,
        honorarios_se_actualizan:
          formData.tipo_obra === "administracion"
            ? formData.honorarios_se_actualizan
            : false,
        metodo_actualizacion_honorarios:
          formData.tipo_obra === "administracion" &&
          formData.honorarios_se_actualizan
            ? formData.metodo_actualizacion_honorarios === "PERSONALIZADO"
              ? formData.nombre_metodo_personalizado_honorarios
              : formData.metodo_actualizacion_honorarios
            : null,
        fecha_inicio_actualizacion_honorarios:
          formData.tipo_obra === "administracion" &&
          formData.honorarios_se_actualizan
            ? formData.fecha_inicio_actualizacion_honorarios || null
            : null,

        // Mano de obra
        mano_obra_separada: formData.mano_obra_separada,
        monto_mano_obra: formData.monto_mano_obra
          ? parseFloat(limpiarFormato(formData.monto_mano_obra))
          : null,
        moneda_mano_obra: formData.mano_obra_separada
          ? formData.moneda_mano_obra
          : null,
        mano_obra_se_actualiza: formData.mano_obra_separada
          ? formData.mano_obra_se_actualiza
          : false,
        metodo_actualizacion_mano_obra:
          formData.mano_obra_separada && formData.mano_obra_se_actualiza
            ? formData.metodo_actualizacion_mano_obra === "PERSONALIZADO"
              ? formData.nombre_metodo_personalizado_mano_obra
              : formData.metodo_actualizacion_mano_obra
            : null,
        fecha_inicio_actualizacion_mano_obra:
          formData.mano_obra_separada && formData.mano_obra_se_actualiza
            ? formData.fecha_inicio_actualizacion_mano_obra || null
            : null,

        // Adicionales
        adicionales_honorarios_habilitado: formData.adicionales_honorarios_habilitado,
        adicionales_tipo_honorarios: formData.adicionales_honorarios_habilitado ? formData.adicionales_tipo_honorarios : null,
        adicionales_porcentaje_honorarios: formData.adicionales_honorarios_habilitado && formData.adicionales_tipo_honorarios === "porcentaje" ? parseFloat(formData.adicionales_porcentaje_honorarios?.replace(/\./g, "") || "0") : null,
        adicionales_monto_fijo_honorarios: formData.adicionales_honorarios_habilitado && formData.adicionales_tipo_honorarios === "monto_fijo" ? parseFloat(formData.adicionales_monto_fijo_honorarios?.replace(/\./g, "") || "0") : null,
        adicionales_moneda_honorarios: formData.adicionales_honorarios_habilitado ? formData.adicionales_moneda_honorarios : null,
        adicionales_actualizar_con_indice: formData.adicionales_honorarios_habilitado ? formData.adicionales_actualizar_con_indice : false,
        adicionales_metodo_actualizacion: formData.adicionales_honorarios_habilitado && formData.adicionales_actualizar_con_indice ? (formData.adicionales_metodo_actualizacion === "PERSONALIZADO" ? formData.adicionales_nombre_metodo_personalizado : formData.adicionales_metodo_actualizacion) : null,
        adicionales_fecha_inicio_actualizacion: formData.adicionales_honorarios_habilitado && formData.adicionales_tipo_honorarios === "monto_fijo" && formData.adicionales_actualizar_con_indice ? (formData.adicionales_fecha_inicio_actualizacion || null) : null,

        fecha_inicio: formData.fecha_inicio || null,
        fecha_finalizacion: formData.fecha_finalizacion || null,
        obra_finalizada: formData.obra_finalizada,
        mostrar_cajas: formData.mostrar_cajas,
        render_url: formData.render_url || null,
        tipo_cambio: formData.tipo_cambio
          ? parseFloat(formData.tipo_cambio)
          : null,
      };

      let result;
      if (proyecto) {
        // Actualizar
        result = await supabase
          .from("proyectos")
          .update(dataToSave)
          .eq("id", proyecto.id)
          .select();
      } else {
        // Crear nuevo
        result = await supabase.from("proyectos").insert([dataToSave]).select();
      }

      const { data, error } = result;

      if (error) {
        console.error("Error guardando proyecto:", error);
        showCustomAlert(
          "error",
          "Error al guardar",
          "Error al guardar: " + error.message
        );
      } else {
        // ✅ Actualizar presupuestos totales automáticamente
        const proyectoId = proyecto ? proyecto.id : data[0]?.id;
        if (proyectoId) {
          // Ejecutar en background sin bloquear la UI
          (async () => {
            try {
              // Actualizar presupuesto total inicial
              await actualizarPresupuestoTotalInicial(proyectoId);
              // Actualizar presupuesto total actualizado
              await actualizarPresupuestoTotalActualizado(proyectoId);
              // ✅ NUEVO: Recalcular tabla presupuestos_iniciales_actualizados
              await recalcularPresupuestosInicialesActualizados(proyectoId);
            } catch (error) {
              console.error("⚠️ Error actualizando presupuestos totales:", error);
              // No mostrar error al usuario, solo log
            }
          })();
        }
        
        showCustomAlert(
          "success",
          "Éxito",
          proyecto
            ? "Proyecto actualizado correctamente"
            : "Proyecto creado correctamente"
        );
        onSave();
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }

    setGuardando(false);
  };

  // Honorarios por porcentaje: estados para mostrar resumen
  const [resumenHonorarios, setResumenHonorarios] = useState({
    totalGastado: 0,
    totalPagado: 0,
    porcentaje: 0,
    totalHonorarios: 0,
    saldo: 0,
    cargando: false,
    error: null,
  });

  useEffect(() => {
    // Solo cargar si es administracion_porcentaje y estamos en el paso 3
    if (
      (formData.tipo_obra === "administracion_porcentaje" ||
        (formData.tipo_obra === "administracion" && formData.tipo_monto_honorario === "%")) &&
      currentStep === 3
    ) {
      setResumenHonorarios((prev) => ({ ...prev, cargando: true, error: null }));
      (async () => {
        try {
          // 1. Total gastado: suma de salidas del proyecto
          let totalGastado = 0;
          const { data: salidas, error: errorSalidas } = await supabase
            .from("salidas")
            .select("monto, moneda, tipo_cambio")
            .eq("proyecto_id", proyecto?.id);
          if (errorSalidas) throw new Error("Error cargando salidas");
          if (salidas) {
            salidas.forEach((s) => {
              const monto = parseFloat(s.monto || 0);
              const tc = parseFloat(s.tipo_cambio || 1);
              totalGastado += s.moneda === "USD" ? monto * tc : monto;
            });
          }

          // 2. Total pagado como honorarios: suma de entradas con concepto honorario
          let totalPagado = 0;
          const { data: entradas, error: errorEntradas } = await supabase
            .from("entradas")
            .select("monto, moneda, tipo_cambio, conceptos_entrada!inner(nombre)")
            .eq("proyecto_id", proyecto?.id)
            .ilike("conceptos_entrada.nombre", "%honorario%");
          if (errorEntradas) throw new Error("Error cargando pagos honorarios");
          if (entradas) {
            entradas.forEach((e) => {
              const monto = parseFloat(e.monto || 0);
              const tc = parseFloat(e.tipo_cambio || 1);
              totalPagado += e.moneda === "USD" ? monto * tc : monto;
            });
          }

          // 3. Porcentaje configurado
          const porcentaje = parseFloat(
            formData.porcentaje_honorario?.replace(",", ".") || 0
          );
          // 4. Total honorarios calculados
          const totalHonorarios = (totalGastado * porcentaje) / 100;
          // 5. Saldo
          const saldo = totalHonorarios - totalPagado;

          setResumenHonorarios({
            totalGastado,
            totalPagado,
            porcentaje,
            totalHonorarios,
            saldo,
            cargando: false,
            error: null,
          });
        } catch (err) {
          setResumenHonorarios((prev) => ({
            ...prev,
            cargando: false,
            error: err.message,
          }));
        }
      })();
    }
  }, [formData.tipo_obra, formData.tipo_monto_honorario, formData.porcentaje_honorario, currentStep, proyecto]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div style={{ display: "grid", gap: "20px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  background: `${colorVista.solid}10`,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FileText size={16} color={colorVista.solid} />
              </div>
              <h3
                style={{
                  margin: 0,
                  color: "#1e293b",
                  fontSize: isMobile ? "18px" : "20px",
                  fontWeight: "600",
                }}
              >
                Información Básica
              </h3>
            </div>

            <div
              style={{
                background: "#f0f9ff",
                padding: "16px",
                borderRadius: "12px",
                border: "1px solid #bae6fd",
              }}
            >
              <p
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#0c4a6e",
                }}
              >
                El código se genera automáticamente:
              </p>
              <p
                style={{
                  margin: 0,
                  fontSize: "12px",
                  color: "#075985",
                }}
              >
                Primeras 3 letras del apellido + número si existe duplicado
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: "16px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Apellido del Cliente *
                </label>
                <input
                  type="text"
                  value={formData.apellido_cliente}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      apellido_cliente: e.target.value,
                    })
                  }
                  placeholder="Ej: García"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "10px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "border-color 0.2s",
                    touchAction: "manipulation",
                    WebkitAppearance: "none",
                    appearance: "none",
                  }}
                  onFocus={(e) =>
                    (e.target.style.borderColor = colorVista.solid)
                  }
                  onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  CUIT del Cliente
                </label>
                <input
                  type="text"
                  value={formData.cuit_cliente}
                  onChange={(e) => {
                    const numeros = e.target.value.replace(/\D/g, "");
                    const limitado = numeros.substring(0, 11);

                    let formateado = "";
                    if (limitado.length <= 2) {
                      formateado = limitado;
                    } else if (limitado.length <= 10) {
                      formateado = `${limitado.substring(
                        0,
                        2
                      )}-${limitado.substring(2)}`;
                    } else {
                      formateado = `${limitado.substring(
                        0,
                        2
                      )}-${limitado.substring(2, 10)}-${limitado.substring(
                        10
                      )}`;
                    }

                    setFormData({ ...formData, cuit_cliente: formateado });
                  }}
                  placeholder="27-12345678-9"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "10px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "border-color 0.2s",
                    fontFamily: "monospace",
                    letterSpacing: "1px",
                    touchAction: "manipulation",
                    WebkitAppearance: "none",
                    appearance: "none",
                  }}
                  onFocus={(e) =>
                    (e.target.style.borderColor = colorVista.solid)
                  }
                  onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                />
                <p
                  style={{
                    fontSize: "11px",
                    color: "#64748b",
                    margin: "4px 0 0 0",
                  }}
                >
                  Solo escriba números, el formato se aplica automáticamente
                </p>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Código del Proyecto{" "}
                  {!proyecto && "(generado automáticamente)"}
                </label>
                <input
                  type="text"
                  value={formData.codigo}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      codigo: e.target.value.toUpperCase(),
                    })
                  }
                  placeholder="GAR"
                  readOnly={!proyecto}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "10px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "border-color 0.2s",
                    background: !proyecto ? "#f8fafc" : "white",
                    color: !proyecto ? "#64748b" : "#1e293b",
                    touchAction: "manipulation",
                    WebkitAppearance: "none",
                    appearance: "none",
                  }}
                  onFocus={(e) =>
                    (e.target.style.borderColor = colorVista.solid)
                  }
                  onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                />
                {!proyecto && (
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#64748b",
                      margin: "4px 0 0 0",
                    }}
                  >
                    Se actualiza automáticamente al escribir el apellido
                  </p>
                )}
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Email del Cliente *
                </label>
                <input
                  type="email"
                  value={formData.email_cliente}
                  onChange={(e) =>
                    setFormData({ ...formData, email_cliente: e.target.value })
                  }
                  placeholder="cliente@email.com"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "10px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "border-color 0.2s",
                    touchAction: "manipulation",
                    WebkitAppearance: "none",
                    appearance: "none",
                  }}
                  onFocus={(e) =>
                    (e.target.style.borderColor = colorVista.solid)
                  }
                  onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Ubicación (opcional)
                </label>
                <textarea
                  value={formData.ubicacion}
                  onChange={(e) =>
                    setFormData({ ...formData, ubicacion: e.target.value })
                  }
                  placeholder="Dirección completa del proyecto..."
                  rows="3"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "10px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "border-color 0.2s",
                    resize: "vertical",
                    touchAction: "manipulation",
                    WebkitAppearance: "none",
                    appearance: "none",
                  }}
                  onFocus={(e) =>
                    (e.target.style.borderColor = colorVista.solid)
                  }
                  onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div style={{ display: "grid", gap: "20px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  background: `${colorVista.solid}10`,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Ruler size={16} color={colorVista.solid} />
              </div>
              <h3
                style={{
                  margin: 0,
                  color: "#1e293b",
                  fontSize: isMobile ? "18px" : "20px",
                  fontWeight: "600",
                }}
              >
                Métricas del Proyecto
              </h3>
            </div>

            <div
              style={{
                background: "#f0f9ff",
                padding: "16px",
                borderRadius: "12px",
                border: "1px solid #bae6fd",
              }}
            >
              <p
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#0c4a6e",
                }}
              >
                Los metros de cómputo se calculan automáticamente:
              </p>
              <p
                style={{
                  margin: 0,
                  fontSize: "12px",
                  color: "#075985",
                }}
              >
                Cubiertos 100% + Semicubiertos 50% + Solados 25%
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                gap: "16px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Metros Cubiertos *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.metros_cubiertos}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      metros_cubiertos: e.target.value,
                    })
                  }
                  placeholder="0.00"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "10px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "border-color 0.2s",
                    touchAction: "manipulation",
                    WebkitAppearance: "none",
                    appearance: "none",
                  }}
                  onFocus={(e) =>
                    (e.target.style.borderColor = colorVista.solid)
                  }
                  onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Metros Semicubiertos
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.metros_semicubiertos}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      metros_semicubiertos: e.target.value,
                    })
                  }
                  placeholder="0.00"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "10px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "border-color 0.2s",
                    touchAction: "manipulation",
                    WebkitAppearance: "none",
                    appearance: "none",
                  }}
                  onFocus={(e) =>
                    (e.target.style.borderColor = colorVista.solid)
                  }
                  onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Metros Solados
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.metros_solados}
                  onChange={(e) =>
                    setFormData({ ...formData, metros_solados: e.target.value })
                  }
                  placeholder="0.00"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "10px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "border-color 0.2s",
                    touchAction: "manipulation",
                    WebkitAppearance: "none",
                    appearance: "none",
                  }}
                  onFocus={(e) =>
                    (e.target.style.borderColor = colorVista.solid)
                  }
                  onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                />
              </div>
            </div>

            <div
              style={{
                background: "#f8fafc",
                padding: "20px",
                borderRadius: "12px",
                border: "2px solid #e2e8f0",
                textAlign: "center",
              }}
            >
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Metros de Cómputo (calculado automáticamente)
              </label>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#059669",
                  background: "white",
                  padding: "12px",
                  borderRadius: "8px",
                  display: "inline-block",
                  minWidth: "120px",
                }}
              >
                {formData.metros_computo || "0.00"} m²
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div style={{ display: "grid", gap: "20px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  background: `${colorVista.solid}10`,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <DollarSign size={16} color={colorVista.solid} />
              </div>
              <h3
                style={{
                  margin: 0,
                  color: "#1e293b",
                  fontSize: isMobile ? "18px" : "20px",
                  fontWeight: "600",
                }}
              >
                Información Financiera
              </h3>
            </div>

            {/* Tipo de obra */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "12px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Tipo de Obra *
              </label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                }}
                >
                <button
                  type="button"
                  onClick={() =>
                    setFormData({ ...formData, tipo_obra: "llave_en_mano" })
                  }
                  style={{
                    padding: "16px",
                    border: `2px solid ${
                      formData.tipo_obra === "llave_en_mano"
                        ? colorVista.solid
                        : "#e2e8f0"
                    }`,
                    borderRadius: "12px",
                    background:
                      formData.tipo_obra === "llave_en_mano"
                        ? `${colorVista.solid}10`
                        : "white",
                    color:
                      formData.tipo_obra === "llave_en_mano"
                        ? colorVista.solid
                        : "#64748b",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    touchAction: "manipulation",
                  }}
                >
                  <FileText size={16} />
                  Llave en Mano
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setFormData({ ...formData, tipo_obra: "administracion" })
                  }
                  style={{
                    padding: "16px",
                    border: `2px solid ${
                      formData.tipo_obra === "administracion" || formData.tipo_obra === "administracion_porcentaje"
                        ? colorVista.solid
                        : "#e2e8f0"
                    }`,
                    borderRadius: "12px",
                    background:
                      formData.tipo_obra === "administracion" || formData.tipo_obra === "administracion_porcentaje"
                        ? `${colorVista.solid}10`
                        : "white",
                    color:
                      formData.tipo_obra === "administracion" || formData.tipo_obra === "administracion_porcentaje"
                        ? colorVista.solid
                        : "#64748b",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    touchAction: "manipulation",
                  }}
                >
                  <FileText size={16} />
                  Administración
                </button>
              </div>
            </div>

            {/* Dropdown Moneda o % solo si es administración */}
            {formData.tipo_obra === "administracion" && (
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Moneda o %
                </label>
                <CustomDropdown
                  value={formData.tipo_monto_honorario}
                  onChange={(value) => {
                    const next = { ...formData, tipo_monto_honorario: value };
                    if (value === "%") {
                      next.tipo_obra = "administracion_porcentaje";
                      next.adicionales_honorarios_habilitado = false;
                    } else if (value === "$") {
                      next.tipo_obra = "administracion";
                    }
                    setFormData(next);
                  }}
                  options={[
                    { value: "$", label: "$ (monto fijo)" },
                    { value: "%", label: "% (porcentaje sobre gastos)" },
                  ]}
                  placeholder="Seleccionar tipo"
                  icon={formData.tipo_monto_honorario === "%" ? Percent : DollarSign}
                />
              </div>
            )}

            {/* Campos específicos según tipo de obra */}
            {formData.tipo_obra === "llave_en_mano" ? (
              <div style={{ display: "grid", gap: "16px" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr",
                    gap: "12px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                      }}
                    >
                      Monto Total *
                    </label>
                    <input
                      type="text"
                      value={formatearNumero(
                        formData.monto_total_llave_en_mano
                      )}
                      onChange={(e) => {
                        const valorLimpio = e.target.value.replace(
                          /[^\d,]/g,
                          ""
                        );
                        setFormData({
                          ...formData,
                          monto_total_llave_en_mano: valorLimpio,
                        });
                      }}
                      placeholder="0,00"
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        border: "2px solid #e2e8f0",
                        borderRadius: "10px",
                        fontSize: "16px",
                        boxSizing: "border-box",
                        touchAction: "manipulation",
                        WebkitAppearance: "none",
                        appearance: "none",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                      }}
                    >
                      Moneda
                    </label>
                    <CustomDropdown
                      value={formData.moneda_llave_en_mano}
                      onChange={(value) =>
                        setFormData({
                          ...formData,
                          moneda_llave_en_mano: value,
                        })
                      }
                      options={[
                        { value: "ARS", label: "ARS" },
                        { value: "USD", label: "USD" },
                      ]}
                      placeholder="Seleccionar moneda"
                      icon={DollarSign}
                    />
                  </div>
                </div>

                {/* Actualización de llave en mano */}
                <div
                  style={{
                    background: "#f0f9ff",
                    padding: "16px",
                    borderRadius: "10px",
                    border: "1px solid #bae6fd",
                  }}
                >
                  <h4
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#0c4a6e",
                      margin: "0 0 12px 0",
                    }}
                  >
                    Actualización por Inflación
                  </h4>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                      gap: "12px",
                    }}
                  >
                    <MetodoActualizacionDropdown
                      value={formData.metodo_actualizacion}
                      onChange={(value) =>
                        setFormData({
                          ...formData,
                          metodo_actualizacion: value,
                        })
                      }
                      label="Método de Actualización"
                      name="metodo_actualizacion"
                    />

                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#374151",
                        }}
                      >
                        Fecha Inicio Actualización
                      </label>
                      <input
                        type="date"
                        value={
                          formData.fecha_inicio_actualizacion_llave_en_mano
                        }
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            fecha_inicio_actualizacion_llave_en_mano:
                              e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          border: "1px solid #d1d5db",
                          borderRadius: "8px",
                          fontSize: "16px",
                          boxSizing: "border-box",
                          touchAction: "manipulation",
                          WebkitAppearance: "none",
                          appearance: "none",
                        }}
                      />
                    </div>
                  </div>

                  {/* Input para método personalizado */}
                  {formData.metodo_actualizacion === "PERSONALIZADO" && (
                    <div style={{ marginTop: "12px" }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#374151",
                        }}
                      >
                        Nombre del Método Personalizado
                      </label>
                      <input
                        type="text"
                        value={formData.nombre_metodo_personalizado}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            nombre_metodo_personalizado: e.target.value,
                          })
                        }
                        placeholder="Ej: Dólar Oficial, UVA, etc."
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          border: "1px solid #d1d5db",
                          borderRadius: "8px",
                          fontSize: "16px",
                          boxSizing: "border-box",
                          touchAction: "manipulation",
                          WebkitAppearance: "none",
                          appearance: "none",
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // ADMINISTRACIÓN
              <div style={{ display: "grid", gap: "16px" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr",
                    gap: "12px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                      }}
                    >
                      {formData.tipo_monto_honorario === "%"
                        ? "Porcentaje Honorario *"
                        : "Honorarios *"}
                    </label>
                    <input
                      type="text"
                      value={
                        formData.tipo_monto_honorario === "%"
                          ? formatearNumero(formData.porcentaje_honorario)
                          : formatearNumero(formData.honorarios)
                      }
                      onChange={(e) => {
                        const valorLimpio = e.target.value.replace(
                          /[^\d,]/g,
                          ""
                        );
                        if (formData.tipo_monto_honorario === "%") {
                          setFormData({
                            ...formData,
                            porcentaje_honorario: valorLimpio,
                          });
                        } else {
                          setFormData({
                            ...formData,
                            honorarios: valorLimpio,
                          });
                        }
                      }}
                      placeholder={
                        formData.tipo_monto_honorario === "%"
                          ? "0,00"
                          : "0,00"
                      }
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        border: "2px solid #e2e8f0",
                        borderRadius: "10px",
                        fontSize: "16px",
                        boxSizing: "border-box",
                        touchAction: "manipulation",
                        WebkitAppearance: "none",
                        appearance: "none",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                      }}
                    >
                      Moneda
                    </label>
                    <CustomDropdown
                      value={formData.moneda_honorarios}
                      onChange={(value) =>
                        setFormData({ ...formData, moneda_honorarios: value })
                      }
                      options={[
                        { value: "ARS", label: "ARS" },
                        { value: "USD", label: "USD" },
                      ]}
                      placeholder="Seleccionar moneda"
                      icon={DollarSign}
                      disabled={formData.tipo_monto_honorario === "%" && formData.tipo_obra !== "administracion" && formData.tipo_obra !== "administracion_porcentaje"}
                    />
                  </div>
                </div>

                {/* Actualización de honorarios */}
                <div
                  style={{
                    background: "#f0f9ff",
                    padding: "16px",
                    borderRadius: "10px",
                    border: "1px solid #bae6fd",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#0c4a6e",
                      marginBottom: formData.honorarios_se_actualizan
                        ? "12px"
                        : "0",
                    }}
                  >
                    <span>Los honorarios se actualizan</span>
                    <button
                      type="button"
                      aria-pressed={Boolean(formData.honorarios_se_actualizan)}
                      onClick={() =>
                        setFormData({
                          ...formData,
                          honorarios_se_actualizan: !formData.honorarios_se_actualizan,
                        })
                      }
                      style={{
                        width: 44,
                        height: 24,
                        borderRadius: 9999,
                        border: '1px solid ' + (formData.honorarios_se_actualizan ? '#22c55e' : '#cbd5e1'),
                        background: formData.honorarios_se_actualizan ? '#22c55e' : '#e2e8f0',
                        position: 'relative',
                        transition: 'all .2s ease',
                        outline: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          top: 2,
                          left: formData.honorarios_se_actualizan ? 22 : 2,
                          width: 18,
                          height: 18,
                          borderRadius: '9999px',
                          background: 'white',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          transition: 'left .2s ease',
                        }}
                      />
                    </button>
                  </div>

                  {formData.honorarios_se_actualizan && (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                        gap: "12px",
                      }}
                    >
                      <MetodoActualizacionDropdown
                        value={formData.metodo_actualizacion_honorarios}
                        onChange={(value) =>
                          setFormData({
                            ...formData,
                            metodo_actualizacion_honorarios: value,
                          })
                        }
                        label="Método de Actualización"
                        name="metodo_actualizacion_honorarios"
                      />

                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#374151",
                          }}
                        >
                          Fecha Inicio Actualización
                        </label>
                        <input
                          type="date"
                          value={formData.fecha_inicio_actualizacion_honorarios}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              fecha_inicio_actualizacion_honorarios:
                                e.target.value,
                            })
                          }
                          style={{
                            width: "100%",
                            padding: "10px 14px",
                            border: "1px solid #d1d5db",
                            borderRadius: "8px",
                            fontSize: "16px",
                            boxSizing: "border-box",
                            touchAction: "manipulation",
                            WebkitAppearance: "none",
                            appearance: "none",
                          }}
                        />
                      </div>

                      {/* Input para método personalizado de honorarios */}
                      {formData.metodo_actualizacion_honorarios ===
                        "PERSONALIZADO" && (
                        <div style={{ gridColumn: isMobile ? "1" : "span 2" }}>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "8px",
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#374151",
                            }}
                          >
                            Nombre del Método Personalizado
                          </label>
                          <input
                            type="text"
                            value={
                              formData.nombre_metodo_personalizado_honorarios
                            }
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                nombre_metodo_personalizado_honorarios:
                                  e.target.value,
                              })
                            }
                            placeholder="Ej: Dólar Oficial, UVA, etc."
                            style={{
                              width: "100%",
                              padding: "10px 14px",
                              border: "1px solid #d1d5db",
                              borderRadius: "8px",
                              fontSize: "16px",
                              boxSizing: "border-box",
                              touchAction: "manipulation",
                              WebkitAppearance: "none",
                              appearance: "none",
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Mano de obra separada */}
            <div
              style={{
                background: "#f8fafc",
                padding: "20px",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                  marginBottom: "16px",
                }}
              >
                <span>Mano de obra separada</span>
                <button
                  type="button"
                  aria-pressed={Boolean(formData.mano_obra_separada)}
                  onClick={() =>
                    setFormData({
                      ...formData,
                      mano_obra_separada: !formData.mano_obra_separada,
                    })
                  }
                  style={{
                    width: 44,
                    height: 24,
                    borderRadius: 9999,
                    border: '1px solid ' + (formData.mano_obra_separada ? '#22c55e' : '#cbd5e1'),
                    background: formData.mano_obra_separada ? '#22c55e' : '#e2e8f0',
                    position: 'relative',
                    transition: 'all .2s ease',
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: 2,
                      left: formData.mano_obra_separada ? 22 : 2,
                      width: 18,
                      height: 18,
                      borderRadius: '9999px',
                      background: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      transition: 'left .2s ease',
                    }}
                  />
                </button>
              </div>

              {formData.mano_obra_separada && (
                <div style={{ display: "grid", gap: "16px" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr",
                      gap: "12px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#374151",
                        }}
                      >
                        Monto Mano de Obra
                      </label>
                      <input
                        type="text"
                        value={formatearNumero(formData.monto_mano_obra)}
                        onChange={(e) => {
                          const valorLimpio = e.target.value.replace(
                            /[^\d,]/g,
                            ""
                          );
                          setFormData({
                            ...formData,
                            monto_mano_obra: valorLimpio,
                          });
                        }}
                        placeholder="0,00"
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          border: "1px solid #d1d5db",
                          borderRadius: "8px",
                          fontSize: "16px",
                          boxSizing: "border-box",
                          touchAction: "manipulation",
                          WebkitAppearance: "none",
                          appearance: "none",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#374151",
                        }}
                      >
                        Moneda
                      </label>
                      <CustomDropdown
                        value={formData.moneda_mano_obra}
                        onChange={(value) =>
                          setFormData({ ...formData, moneda_mano_obra: value })
                        }
                        options={[
                          { value: "ARS", label: "ARS" },
                          { value: "USD", label: "USD" },
                        ]}
                        placeholder="Seleccionar moneda"
                        icon={DollarSign}
                      />
                    </div>
                  </div>

                  {/* Actualización de mano de obra */}
                  <div
                    style={{
                      background: "#f0f9ff",
                      padding: "16px",
                      borderRadius: "10px",
                      border: "1px solid #bae6fd",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#0c4a6e",
                        marginBottom: formData.mano_obra_se_actualiza
                          ? "12px"
                          : "0",
                      }}
                    >
                      <span>Mano de obra se actualiza</span>
                      <button
                        type="button"
                        aria-pressed={Boolean(formData.mano_obra_se_actualiza)}
                        onClick={() =>
                          setFormData({
                            ...formData,
                            mano_obra_se_actualiza: !formData.mano_obra_se_actualiza,
                          })
                        }
                        style={{
                          width: 44,
                          height: 24,
                          borderRadius: 9999,
                          border: '1px solid ' + (formData.mano_obra_se_actualiza ? '#22c55e' : '#cbd5e1'),
                          background: formData.mano_obra_se_actualiza ? '#22c55e' : '#e2e8f0',
                          position: 'relative',
                          transition: 'all .2s ease',
                          outline: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        <span
                          style={{
                            position: 'absolute',
                            top: 2,
                            left: formData.mano_obra_se_actualiza ? 22 : 2,
                            width: 18,
                            height: 18,
                            borderRadius: '9999px',
                            background: 'white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            transition: 'left .2s ease',
                          }}
                        />
                      </button>
                    </div>

                    {formData.mano_obra_se_actualiza && (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                          gap: "12px",
                        }}
                      >
                        <MetodoActualizacionDropdown
                          value={formData.metodo_actualizacion_mano_obra}
                          onChange={(value) =>
                            setFormData({
                              ...formData,
                              metodo_actualizacion_mano_obra: value,
                            })
                          }
                          label="Método de Actualización"
                          name="metodo_actualizacion_mano_obra"
                        />

                        <div>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "8px",
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#374151",
                            }}
                          >
                            Fecha Inicio Actualización
                          </label>
                          <input
                            type="date"
                            value={
                              formData.fecha_inicio_actualizacion_mano_obra
                            }
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                fecha_inicio_actualizacion_mano_obra:
                                  e.target.value,
                              })
                            }
                            style={{
                              width: "100%",
                              padding: "10px 14px",
                              border: "1px solid #d1d5db",
                              borderRadius: "8px",
                              fontSize: "16px",
                              boxSizing: "border-box",
                              touchAction: "manipulation",
                              WebkitAppearance: "none",
                              appearance: "none",
                            }}
                          />
                        </div>

                        {/* Input para método personalizado de mano de obra */}
                        {formData.metodo_actualizacion_mano_obra ===
                          "PERSONALIZADO" && (
                          <div
                            style={{ gridColumn: isMobile ? "1" : "span 2" }}
                          >
                            <label
                              style={{
                                display: "block",
                                marginBottom: "8px",
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#374151",
                              }}
                            >
                              Nombre del Método Personalizado
                            </label>
                            <input
                              type="text"
                              value={
                                formData.nombre_metodo_personalizado_mano_obra
                              }
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  nombre_metodo_personalizado_mano_obra:
                                    e.target.value,
                                })
                              }
                              placeholder="Ej: Dólar Oficial, UVA, etc."
                              style={{
                                width: "100%",
                                padding: "10px 14px",
                                border: "1px solid #d1d5db",
                                borderRadius: "8px",
                                fontSize: "16px",
                                boxSizing: "border-box",
                                touchAction: "manipulation",
                                WebkitAppearance: "none",
                                appearance: "none",
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Configuración de Adicionales */}
            <div
              style={{
                background: "#f8fafc",
                padding: isMobile ? "16px" : "20px",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
              }}
            >
              {(() => {
                const esPorcentaje =
                  formData.tipo_obra === "administracion_porcentaje" ||
                  (formData.tipo_obra === "administracion" &&
                    formData.tipo_monto_honorario === "%");
                return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                  marginBottom: formData.adicionales_honorarios_habilitado ? "16px" : "0",
                  position: "relative",
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Package size={14} />
                  Cobrar honorarios por gastos adicionales
                </span>
                <div 
                  style={{ position: "relative", display: "inline-block" }}
                  onMouseEnter={() => esPorcentaje && setShowTooltipAdic(true)}
                  onMouseLeave={() => setShowTooltipAdic(false)}
                >
                  <button
                    type="button"
                    aria-pressed={Boolean(formData.adicionales_honorarios_habilitado)}
                    disabled={esPorcentaje}
                    onClick={() => {
                      if (esPorcentaje) {
                        showCustomAlert(
                          "info",
                          "No disponible",
                          "Los honorarios por adicionales no aplican cuando los honorarios generales son porcentaje sobre gastos, ya que los adicionales están incluidos en ese porcentaje."
                        );
                        return;
                      }
                      setFormData((prev) => ({
                        ...prev,
                        adicionales_honorarios_habilitado: !prev.adicionales_honorarios_habilitado,
                      }));
                    }}
                    style={{
                      width: 44,
                      height: 24,
                      opacity: esPorcentaje ? 0.4 : 1,
                      cursor: esPorcentaje ? "not-allowed" : "pointer",
                      borderRadius: 9999,
                      border: '1px solid ' + (formData.adicionales_honorarios_habilitado ? '#22c55e' : '#cbd5e1'),
                      background: formData.adicionales_honorarios_habilitado ? '#22c55e' : '#e2e8f0',
                      position: 'relative',
                      transition: 'all .2s ease',
                      outline: 'none',
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        top: 2,
                        left: formData.adicionales_honorarios_habilitado ? 22 : 2,
                        width: 18,
                        height: 18,
                        borderRadius: '9999px',
                        background: 'white',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        transition: 'left .2s ease',
                      }}
                    />
                  </button>
                  {esPorcentaje && showTooltipAdic && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '-36px',
                        right: 0,
                        background: '#111827',
                        color: 'white',
                        fontSize: '11px',
                        padding: '6px 8px',
                        borderRadius: '6px',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
                      }}
                    >
                      Los honorarios por adicionales no aplican cuando los honorarios generales son % sobre gastos.
                    </div>
                  )}
                </div>
              </div>
              ); })()}

              {formData.adicionales_honorarios_habilitado && (
                <div style={{ display: "grid", gap: "16px" }}>
                  {/* Tipo de honorarios */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                      gap: "12px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#374151",
                        }}
                      >
                        Tipo de Honorarios
                      </label>
                      <CustomDropdown
                        value={formData.adicionales_tipo_honorarios}
                        onChange={(value) =>
                          setFormData({
                            ...formData,
                            adicionales_tipo_honorarios: value,
                          })
                        }
                        options={[
                          { value: "porcentaje", label: "Porcentaje sobre gastos adicionales" },
                          { value: "monto_fijo", label: "Monto fijo" },
                        ]}
                        placeholder="Seleccionar tipo"
                        icon={formData.adicionales_tipo_honorarios === "porcentaje" ? Percent : DollarSign}
                      />
                    </div>

                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#374151",
                        }}
                      >
                        Moneda
                      </label>
                      <CustomDropdown
                        value={formData.adicionales_moneda_honorarios}
                        onChange={(value) =>
                          setFormData({
                            ...formData,
                            adicionales_moneda_honorarios: value,
                          })
                        }
                        options={[
                          { value: "ARS", label: "ARS" },
                          { value: "USD", label: "USD" },
                        ]}
                        placeholder="Seleccionar moneda"
                        icon={DollarSign}
                      />
                    </div>
                  </div>

                  {/* Monto o porcentaje */}
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#374151",
                      }}
                    >
                      {formData.adicionales_tipo_honorarios === "porcentaje" 
                        ? "Porcentaje (%)" 
                        : "Monto Fijo"}
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={
                        formData.adicionales_tipo_honorarios === "porcentaje"
                          ? formData.adicionales_porcentaje_honorarios
                          : formData.adicionales_monto_fijo_honorarios
                      }
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^\d.]/g, "");
                        const numericValue = value.replace(/\./g, "");
                        const formattedValue = numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
                        
                        setFormData({
                          ...formData,
                          [formData.adicionales_tipo_honorarios === "porcentaje"
                            ? "adicionales_porcentaje_honorarios"
                            : "adicionales_monto_fijo_honorarios"]: formattedValue,
                        });
                      }}
                      placeholder={
                        formData.adicionales_tipo_honorarios === "porcentaje" 
                          ? "0.00" 
                          : "0.00"
                      }
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "16px",
                        boxSizing: "border-box",
                        touchAction: "manipulation",
                      }}
                    />
                  </div>

                  {/* Actualización con índice */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: formData.adicionales_actualizar_con_indice ? "12px" : "0",
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <TrendingUp size={12} />
                      Actualizar con índice
                    </span>
                    <button
                      type="button"
                      aria-pressed={Boolean(formData.adicionales_actualizar_con_indice)}
                      onClick={() => {
                        setFormData((prev) => {
                          console.log("🔍 DEBUG ADICIONALES - Estado actual:", {
                            actualizar_con_indice: prev.adicionales_actualizar_con_indice,
                            tipo_honorarios: prev.adicionales_tipo_honorarios,
                            nuevo_actualizar: !prev.adicionales_actualizar_con_indice
                          });
                          return {
                            ...prev,
                            adicionales_actualizar_con_indice: !prev.adicionales_actualizar_con_indice,
                          };
                        });
                      }}
                      style={{
                        width: 44,
                        height: 24,
                        borderRadius: 9999,
                        border: '1px solid ' + (formData.adicionales_actualizar_con_indice ? '#22c55e' : '#cbd5e1'),
                        background: formData.adicionales_actualizar_con_indice ? '#22c55e' : '#e2e8f0',
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'all .2s ease',
                        outline: 'none',
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          top: 2,
                          left: formData.adicionales_actualizar_con_indice ? 22 : 2,
                          width: 18,
                          height: 18,
                          borderRadius: '9999px',
                          background: 'white',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          transition: 'left .2s ease',
                        }}
                      />
                    </button>
                  </div>

                  {formData.adicionales_actualizar_con_indice && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      {console.log("🔍 DEBUG ADICIONALES - Renderizando bloque índice:", {
                        actualizar_con_indice: formData.adicionales_actualizar_con_indice,
                        tipo_honorarios: formData.adicionales_tipo_honorarios,
                        debe_mostrar_fecha: formData.adicionales_tipo_honorarios === "monto_fijo"
                      })}
                      <MetodoActualizacionDropdown
                        value={formData.adicionales_metodo_actualizacion}
                        onChange={(value) =>
                          setFormData({
                            ...formData,
                            adicionales_metodo_actualizacion: value,
                          })
                        }
                        label="Método de actualización"
                        name="adicionales_metodo_actualizacion"
                        // Reutilizar método personalizado si ya existe en honorarios
                        reutilizarPersonalizado={
                          formData.nombre_metodo_personalizado_honorarios && 
                          formData.nombre_metodo_personalizado_honorarios !== ""
                        }
                        metodoPersonalizadoExistente={formData.nombre_metodo_personalizado_honorarios}
                      />

                      {/* Input para método personalizado de adicionales */}
                      {formData.adicionales_metodo_actualizacion === "PERSONALIZADO" && (
                        <div>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "8px",
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#374151",
                            }}
                          >
                            Nombre del Método Personalizado
                          </label>
                          <input
                            type="text"
                            value={formData.adicionales_nombre_metodo_personalizado}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                adicionales_nombre_metodo_personalizado: e.target.value,
                              })
                            }
                            placeholder="Ej: Dólar Oficial, UVA, etc."
                            style={{
                              width: "100%",
                              padding: "10px 14px",
                              border: "1px solid #d1d5db",
                              borderRadius: "8px",
                              fontSize: "16px",
                              boxSizing: "border-box",
                              touchAction: "manipulation",
                            }}
                          />
                        </div>
                      )}

                      {/* Fecha de inicio para monto fijo */}
                      {formData.adicionales_tipo_honorarios === "monto_fijo" && (
                        <div>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "8px",
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#374151",
                            }}
                          >
                            Fecha de inicio de actualización
                          </label>
                          <input
                            type="date"
                            value={formData.adicionales_fecha_inicio_actualizacion || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                adicionales_fecha_inicio_actualizacion: e.target.value,
                              })
                            }
                            style={{
                              width: "100%",
                              padding: "10px 14px",
                              border: "1px solid #d1d5db",
                              borderRadius: "8px",
                              fontSize: "16px",
                              boxSizing: "border-box",
                              touchAction: "manipulation",
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Fechas del proyecto */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "12px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Fecha de Inicio
                </label>
                <input
                  type="date"
                  value={formData.fecha_inicio}
                  onChange={(e) =>
                    setFormData({ ...formData, fecha_inicio: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "10px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    touchAction: "manipulation",
                    WebkitAppearance: "none",
                    appearance: "none",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Fecha de Finalización
                </label>
                <input
                  type="date"
                  value={formData.fecha_finalizacion}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      fecha_finalizacion: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "10px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    touchAction: "manipulation",
                    WebkitAppearance: "none",
                    appearance: "none",
                  }}
                />
              </div>
            </div>

            {/* Campo tipo de cambio */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Tipo de Cambio (USD → ARS)
              </label>
              <input
                type="text"
                value={formData.tipo_cambio}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, "");
                  setFormData({ ...formData, tipo_cambio: value });
                }}
                placeholder="Ej: 900.1234"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #e2e8f0",
                  borderRadius: "10px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "border-color 0.2s",
                  touchAction: "manipulation",
                  WebkitAppearance: "none",
                  appearance: "none",
                }}
                onFocus={(e) => (e.target.style.borderColor = colorVista.solid)}
                onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
              />
              <p
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  margin: "4px 0 0 0",
                }}
              >
                Valor por defecto: dólar oficial venta (DolarAPI). Puedes
                modificarlo si lo deseas.
              </p>
            </div>
          </div>
        );

      case 4:
        return (
          <div style={{ display: "grid", gap: "20px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  background: `${colorVista.solid}10`,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Upload size={16} color={colorVista.solid} />
              </div>
              <h3
                style={{
                  margin: 0,
                  color: "#1e293b",
                  fontSize: isMobile ? "18px" : "20px",
                  fontWeight: "600",
                }}
              >
                Render y Configuración Final
              </h3>
            </div>

            {/* Upload de render */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "12px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Render del Proyecto
              </label>

              <div
                style={{
                  display: "grid",
                  gap: "16px",
                  gridTemplateColumns: isMobile ? "1fr" : "auto 1fr",
                  alignItems: "start",
                }}
              >
                {/* Preview del render */}
                <div
                  style={{
                    width: isMobile ? "100%" : "200px",
                    height: isMobile ? "200px" : "150px",
                    border: "2px dashed #cbd5e1",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#f8fafc",
                    overflow: "hidden",
                  }}
                >
                  {formData.render_url ? (
                    <img
                      src={formData.render_url}
                      alt="Render"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        console.error(
                          "Error cargando imagen:",
                          formData.render_url
                        );
                        e.target.style.display = "none";
                        e.target.parentElement.innerHTML = `
                          <div style="text-align: center; color: #ef4444; font-size: 14px;">
                            <div style="font-size: 32px; margin-bottom: 8px;">❌</div>
                            Error cargando imagen
                          </div>
                        `;
                      }}
                      onLoad={(e) => {
                        console.log(
                          "Imagen cargada correctamente:",
                          formData.render_url
                        );
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        color: "#64748b",
                        fontSize: "14px",
                      }}
                    >
                      <div
                        style={{
                          width: "48px",
                          height: "48px",
                          background: "#e2e8f0",
                          borderRadius: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto 8px",
                        }}
                      >
                        <Upload size={24} color="#64748b" />
                      </div>
                      Sin render
                    </div>
                  )}
                </div>

                {/* Controles de upload */}
                <div>
                  <input
                    type="file"
                    id="render-upload"
                    accept="image/*"
                    onChange={handleRenderUpload}
                    style={{ display: "none" }}
                    disabled={subiendoRender}
                  />

                  <label
                    htmlFor="render-upload"
                    style={{
                      display: "inline-block",
                      padding: "12px 20px",
                      background: subiendoRender
                        ? "#94a3b8"
                        : colorVista.gradient,
                      color: "white",
                      borderRadius: "10px",
                      fontSize: "14px",
                      fontWeight: "600",
                      cursor: subiendoRender ? "not-allowed" : "pointer",
                      marginBottom: "12px",
                      display: "block",
                      textAlign: "center",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      touchAction: "manipulation",
                    }}
                  >
                    {subiendoRender ? (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                        }}
                      >
                        <Loader2 size={16} className="loading-pulse" />
                        Subiendo...
                      </span>
                    ) : (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                        }}
                      >
                        <Upload size={16} />
                        Seleccionar Render
                      </span>
                    )}
                  </label>

                  <div
                    style={{
                      fontSize: "12px",
                      color: "#64748b",
                      lineHeight: "1.4",
                    }}
                  >
                    <p style={{ margin: "0 0 4px 0" }}>
                      • Formatos: PNG, JPG, JPEG
                    </p>
                    <p style={{ margin: "0 0 4px 0" }}>• Tamaño máximo: 10MB</p>
                    <p style={{ margin: "0" }}>• Recomendado: 1920x1080px</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Configuraciones finales */}
            <div
              style={{
                background: "#f8fafc",
                padding: "20px",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
              }}
            >
              <h4
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Configuraciones
              </h4>

              <div style={{ display: "grid", gap: "12px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#374151",
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <CheckCircle2 size={16} />
                    Obra finalizada
                  </span>
                  <button
                    type="button"
                    aria-pressed={Boolean(formData.obra_finalizada)}
                    onClick={() =>
                      setFormData({
                        ...formData,
                        obra_finalizada: !formData.obra_finalizada,
                      })
                    }
                    style={{
                      width: 44,
                      height: 24,
                      borderRadius: 9999,
                      border: '1px solid ' + (formData.obra_finalizada ? '#22c55e' : '#cbd5e1'),
                      background: formData.obra_finalizada ? '#22c55e' : '#e2e8f0',
                      position: 'relative',
                      transition: 'all .2s ease',
                      outline: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        top: 2,
                        left: formData.obra_finalizada ? 22 : 2,
                        width: 18,
                        height: 18,
                        borderRadius: '9999px',
                        background: 'white',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        transition: 'left .2s ease',
                      }}
                    />
                  </button>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#374151",
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <DollarSign size={16} />
                    Mostrar cajas en operaciones
                  </span>
                  <button
                    type="button"
                    aria-pressed={Boolean(formData.mostrar_cajas)}
                    onClick={() =>
                      setFormData({
                        ...formData,
                        mostrar_cajas: !formData.mostrar_cajas,
                      })
                    }
                    style={{
                      width: 44,
                      height: 24,
                      borderRadius: 9999,
                      border: '1px solid ' + (formData.mostrar_cajas ? '#22c55e' : '#cbd5e1'),
                      background: formData.mostrar_cajas ? '#22c55e' : '#e2e8f0',
                      position: 'relative',
                      transition: 'all .2s ease',
                      outline: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        top: 2,
                        left: formData.mostrar_cajas ? 22 : 2,
                        width: 18,
                        height: 18,
                        borderRadius: '9999px',
                        background: 'white',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        transition: 'left .2s ease',
                      }}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Resumen del proyecto */}
            <div
              style={{
                background: "linear-gradient(135deg, #f0f4ff 0%, #e0f2fe 100%)",
                padding: "20px",
                borderRadius: "12px",
                border: "1px solid #bae6fd",
              }}
            >
              <h4
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#0c4a6e",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FileText size={16} />
                Resumen del Proyecto
              </h4>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                  gap: "12px",
                  fontSize: "13px",
                }}
              >
                <div style={{ color: "#075985" }}>
                  <strong>Código:</strong> {formData.codigo || "Sin definir"}
                </div>
                <div style={{ color: "#075985" }}>
                  <strong>Cliente:</strong>{" "}
                  {formData.apellido_cliente || "Sin definir"}
                </div>
                <div style={{ color: "#075985" }}>
                  <strong>Tipo:</strong>{" "}
                  {getTipoObraLabel(formData)}
                </div>
                <div style={{ color: "#075985" }}>
                  <strong>M² Cómputo:</strong> {formData.metros_computo || "0"}{" "}
                  m²
                </div>
                {formData.monto_total_llave_en_mano && (
                  <div style={{ color: "#075985" }}>
                    <strong>Monto:</strong> {formData.moneda_llave_en_mano}{" "}
                    {Number(formData.monto_total_llave_en_mano).toLocaleString()}
                  </div>
                )}
                {formData.tipo_obra === "administracion_porcentaje" && formData.porcentaje_honorario && (
                  <div style={{ color: "#075985" }}>
                    <strong>Porcentaje Honorario:</strong> {formData.porcentaje_honorario}%
                  </div>
                )}
                {formData.tipo_obra === "administracion" && formData.honorarios && (
                  <div style={{ color: "#075985" }}>
                    <strong>Honorarios:</strong> {formData.moneda_honorarios} {Number(formData.honorarios).toLocaleString()}
                    {formData.honorarios_se_actualizan &&
                      ` (${formData.metodo_actualizacion_honorarios})`}
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // (El bloqueo de scroll ahora se maneja arriba, en Proyectos, usando showModal/customModal)

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: isMobile ? "10px 10px 80px 10px" : "40px",
        overflow: "hidden",
      }}
      onClick={(e) => {
        // Cerrar modal si se hace click en el overlay
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "800px",
          maxHeight: isMobile ? "calc(100vh - 160px)" : "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
        }}
      >
        {/* Header del modal */}
        <div
          style={{
            background: colorVista.gradient,
            color: "white",
            padding: isMobile ? "20px" : "25px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <h2
              style={{
                margin: "0 0 5px 0",
                fontSize: isMobile ? "20px" : "24px",
                fontWeight: "700",
              }}
            >
              {proyecto ? "Editar Proyecto" : "Nuevo Proyecto"}
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                opacity: 0.9,
              }}
            >
              {proyecto
                ? `Modificando ${proyecto.codigo}`
                : "Crear un nuevo proyecto paso a paso"}
            </p>
          </div>

          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "10px",
              padding: "8px 12px",
              color: "white",
              fontSize: "16px",
              cursor: "pointer",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              touchAction: "manipulation",
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Progress bar */}
        <div
          style={{
            background: "#f8fafc",
            padding: isMobile ? "15px 20px" : "20px 25px",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "10px",
            }}
          >
            {steps.map((step, index) => (
              <div
                key={step.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                }}
              >
                <div
                  style={{
                    width: isMobile ? "32px" : "40px",
                    height: isMobile ? "32px" : "40px",
                    borderRadius: "50%",
                    background:
                      currentStep >= step.id ? colorVista.gradient : "#2563eb",
                    color: currentStep >= step.id ? "white" : "#64748b",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: isMobile ? "14px" : "16px",
                    fontWeight: "600",
                    marginRight: index < steps.length - 1 ? "10px" : "0",
                  }}
                >
                  {currentStep > step.id ? (
                    <CheckCircle2 size={isMobile ? 16 : 20} />
                  ) : (
                    step.id
                  )}
                </div>

                {index < steps.length - 1 && (
                  <div
                    style={{
                      flex: 1,
                      height: "3px",
                      background:
                        currentStep > step.id ? colorVista.gradient : "#e2e8f0",
                      marginRight: "10px",
                    }}
                  ></div>
                )}
              </div>
            ))}
          </div>

          <div
            style={{
              display: isMobile ? "none" : "flex",
              justifyContent: "space-between",
            }}
          >
            {steps.map((step) => {
              const IconComponent = step.icon;
              return (
                <div
                  key={step.id}
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color:
                      currentStep >= step.id ? colorVista.solid : "#2563eb",
                    textAlign: "center",
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                  }}
                >
                  <IconComponent size={14} />
                  {step.title}
                </div>
              );
            })}
          </div>

          {isMobile && (
            <div
              style={{
                textAlign: "center",
                marginTop: "8px",
              }}
            >
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: colorVista.solid,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                }}
              >
                {React.createElement(
                  steps.find((s) => s.id === currentStep)?.icon || FileText,
                  { size: 14 }
                )}
                {steps.find((s) => s.id === currentStep)?.title}
              </span>
            </div>
          )}
        </div>

        {/* Contenido del paso */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? "20px" : "25px",
            overflowY: "auto",
          }}
        >
          {renderStepContent()}
        </div>

        {/* Footer con botones */}
        <div
          style={{
            background: "#f8fafc",
            padding: isMobile ? "15px 20px" : "20px 25px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <div style={{ display: "flex", gap: "12px" }}>
            {currentStep > 1 && (
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                style={{
                  padding: isMobile ? "10px 16px" : "12px 20px",
                  border: "2px solid #e2e8f0",
                  borderRadius: "10px",
                  background: "white",
                  color: "#64748b",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  touchAction: "manipulation",
                }}
              >
                <ChevronDown size={16} style={{ transform: "rotate(90deg)" }} />
                Anterior
              </button>
            )}
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <button
              onClick={onClose}
              style={{
                padding: isMobile ? "10px 16px" : "12px 20px",
                border: "2px solid #e2e8f0",
                borderRadius: "10px",
                background: "white",
                color: "#64748b",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                touchAction: "manipulation",
              }}
            >
              Cancelar
            </button>

            {currentStep < 4 ? (
              <button
                onClick={handleNext}
                style={{
                  padding: isMobile ? "10px 16px" : "12px 20px",
                  border: "none",
                  borderRadius: "10px",
                  background: colorVista.gradient,
                  color: "white",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  touchAction: "manipulation",
                }}
              >
                Siguiente
                <ChevronDown
                  size={16}
                  style={{ transform: "rotate(-90deg)" }}
                />
              </button>
            ) : (
              <button
                onClick={handleSave}
                disabled={guardando || subiendoRender}
                style={{
                  padding: isMobile ? "10px 20px" : "12px 24px",
                  border: "none",
                  borderRadius: "10px",
                  background:
                    guardando || subiendoRender
                      ? "#94a3b8"
                      : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor:
                    guardando || subiendoRender ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  touchAction: "manipulation",
                }}
              >
                {guardando ? (
                  <>
                    <Loader2 size={16} className="loading-pulse" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Guardar Proyecto
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ✅ DROPDOWN PARA MÉTODOS DE ACTUALIZACIÓN
function MetodoActualizacionDropdown({ 
  value, 
  onChange, 
  label, 
  name, 
  reutilizarPersonalizado = false, 
  metodoPersonalizadoExistente = "" 
}) {
  const opciones = [
    { value: "", label: "Sin actualización" },
    { value: "CAC_GENERAL", label: "CAC General" },
    { value: "CAC_MATERIALES", label: "CAC Materiales" },
    { value: "CAC_MANO_OBRA", label: "CAC Mano de Obra" },
    { value: "PERSONALIZADO", label: "Personalizado" },
  ];

  // Si hay un método personalizado existente, agregarlo como opción
  if (reutilizarPersonalizado && metodoPersonalizadoExistente) {
    opciones.push({ 
      value: metodoPersonalizadoExistente, 
      label: `Reutilizar: ${metodoPersonalizadoExistente}` 
    });
  }

  return (
    <div>
      <label
        style={{
          display: "block",
          marginBottom: "8px",
          fontSize: "13px",
          fontWeight: "600",
          color: "#374151",
        }}
      >
        {label}
      </label>
      <CustomDropdown
        value={value}
        onChange={onChange}
        options={opciones}
        placeholder="Seleccionar método"
        icon={Activity}
      />
    </div>
  );
}

// Helper para mostrar el tipo de obra amigable
function getTipoObraLabel(proyecto) {
  if (proyecto.tipo_obra === "llave_en_mano") return "Llave en Mano";
  if (proyecto.tipo_obra === "administracion") {
    if (proyecto.porcentaje_honorario) return "Administración (% sobre gastos)";
    return "Administración (monto fijo)";
  }
  return proyecto.tipo_obra;
}

export default Proyectos;
